import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { styles, colors, StatusPill, fmtDate } from './UI';
import DropZone from './DropZone';
import { useTableControls, SortableFilterTh, ActiveFiltersBar } from './TableControls';
import { useRollup, RollupH2 } from './Rollup';

// Equipment Gantt (phase 1 of the equipment pipeline, 2026-06-11).
// Operations-side demand planning: projects flipped onto the Gantt render as
// timeline bars; their equipment lines aggregate into a per-CAT-CLASS demand
// table compared against companywide Rental Man stock (2-hourly report in
// R2). Bidding projects flipped on are "scenarios" — ghosted bars, demand
// included only while the Include-scenarios toggle is on — which is the
// what-if mechanism: flip a big bid on, read the shortage column, price the
// re-rents into the quote, flip it back off. Desktop-only like the rest of
// Operations.

// ---- date helpers (all string math on YYYY-MM-DD, no TZ surprises) ----
const toUTC = (iso) => new Date(iso + 'T00:00:00Z');
const isoOf = (d) => d.toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = toUTC(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return isoOf(d);
};
const daysBetween = (a, b) => Math.round((toUTC(b) - toUTC(a)) / 86400000);
const todayIso = () => new Date().toISOString().slice(0, 10);
// First of the current month, offset by N months (for the rolling window).
const monthStartIso = (offset = 0) => {
  const d = toUTC(todayIso());
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + offset);
  return isoOf(d);
};

// Project bar window: planned start (today when unset) + duration (30-day
// assumption matches the status auto-transition cron).
const projWindow = (p) => {
  const start = p.date_planned || todayIso();
  const end = addDays(start, Math.max(1, p.duration_days || 30));
  return { start, end };
};

// On-site FOOTPRINT = first labor in → last labor out (pre-TA mobilization
// through post-TA demob). This is when equipment and crew are actually on the
// job — NOT date_planned/duration (the mechanical window). Equipment is
// delivered pre-TA and removed post-TA, so the footprint drives the schedule
// bars, the timeline window, and the availability math. Falls back to
// projWindow only when a project has no staffed labor lines.
const projFootprint = (p) => {
  let lo = null, hi = null;
  for (const l of (p.labor_lines || [])) {
    if ((Number(l.day_hc) || 0) + (Number(l.night_hc) || 0) === 0) continue;
    if (l.start_date && (!lo || l.start_date < lo)) lo = l.start_date;
    if (l.end_date && (!hi || l.end_date > hi)) hi = l.end_date;
  }
  return (lo && hi) ? { start: lo, end: hi } : projWindow(p);
};

// A line inherits missing dates from its project: estimate lines may carry
// num_days instead of an end date.
const lineWindow = (line, proj) => {
  const pw = projFootprint(proj);
  const start = line.start_date || pw.start;
  let end = line.end_date;
  if (!end && line.num_days) end = addDays(start, Math.max(1, line.num_days));
  if (!end) end = pw.end;
  if (daysBetween(start, end) < 1) end = addDays(start, 1);
  return { start, end };
};

// Return/transport buffer (George 2026-06-13, wired into availability math
// 2026-07-08): owned equipment coming off a job is NOT rent-ready the next
// day — it has to travel back and be flipped. Availability/demand-vs-stock
// math holds each owned line for RETURN_BUFFER_DAYS past its on-site end.
// Display bars and day grids keep showing the real on-site window; only the
// occupied-fleet math is extended. Third-party rentals are the vendor's
// logistics, no buffer. Same constant server-side in masterGanttWorkbook.js.
const RETURN_BUFFER_DAYS = 10;
const bufEnd = (endIso) => addDays(endIso, RETURN_BUFFER_DAYS);

// Peak concurrent demand via event sweep: +qty at start, -qty at end.
const peakOf = (intervals) => {
  const events = [];
  for (const { start, end, qty } of intervals) {
    events.push([start, qty], [end, -qty]);
  }
  events.sort((a, b) => a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1] - b[1]);
  let cur = 0, peak = 0;
  for (const [, delta] of events) { cur += delta; if (cur > peak) peak = cur; }
  return peak;
};

const downloadBlob = async (resPromise, fallbackName) => {
  const res = await resPromise;
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename="([^"]+)"/.exec(cd);
  a.download = m ? m[1] : fallbackName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

export default function EquipmentGantt() {
  const [projects, setProjects] = useState([]);
  const [stock, setStock] = useState(null);       // { items, report_last_modified, stale } | null
  const [stockError, setStockError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [includeScenarios, setIncludeScenarios] = useState(true);
  const [viewMode, setViewMode] = useState('span'); // 'span' (oldest active → farthest pending) | 'rolling' (fixed 12-mo)
  const [expandedId, setExpandedId] = useState(null);
  const [focusId, setFocusId] = useState(null);    // focus the whole page on one job (null = all jobs)
  const [locDetail, setLocDetail] = useState(null); // { cat_class, description, avail_by_branch } | null
  const [availDate, setAvailDate] = useState(todayIso()); // date-phased availability lookup
  const [uploadResult, setUploadResult] = useState(null);
  const [mgBusy, setMgBusy] = useState(false); // Master Gantt export in flight
  const fileRef = useRef(null);
  const dateTouchedRef = useRef(false); // stop auto-defaulting the availability date once the user picks one
  // Rollups — one per section card. This page is the longest in Operations,
  // so rolling up the sections you aren't using is the point of the feature.
  const [rolledSched, toggleSched] = useRollup('gantt-schedule');
  const [rolledTimeline, toggleTimeline] = useRollup('gantt-avail-timeline');
  const [rolledDate, toggleDate] = useRollup('gantt-avail-date');
  const [rolledDemand, toggleDemand] = useRollup('gantt-demand');
  const [rolledLabor, toggleLabor] = useRollup('gantt-manpower');

  const reload = async () => {
    setError('');
    try {
      const feed = await api.ganttFeed();
      setProjects(feed.projects || []);
    } catch (e) { setError(e.message); }
    try {
      setStock(await api.ganttStock());
      setStockError('');
    } catch (e) {
      setStock(null);
      setStockError(e.message);
    }
  };
  useEffect(() => { reload(); }, []);

  const onGantt = useMemo(
    () => includeScenarios ? projects : projects.filter(p => !p.scenario),
    [projects, includeScenarios]
  );
  // Focus the whole page (schedule, window, and every analysis card) on one job,
  // or "All jobs" for the master roll-up. Without this, a craft or cat-class that
  // spans several turnarounds reads as one blended demand — and at ~20 jobs the
  // roll-up is unreadable. Everything downstream already keys off `visible`, so
  // making `visible` the focused set scopes the entire page in one place.
  const visible = useMemo(() => {
    if (focusId == null) return onGantt;
    const one = onGantt.filter(p => p.id === focusId);
    return one.length ? one : onGantt;   // focused job toggled off / hidden → fall back to all
  }, [onGantt, focusId]);

  // Land the availability date on a day that actually has equipment deployed:
  // today if a job is on-site today, otherwise the earliest job start. Most
  // jobs are future, so today is usually empty — jumping to the first
  // deployment makes the table populated on load. Stops once the user picks.
  useEffect(() => {
    if (dateTouchedRef.current || !visible.length) return;
    const today = todayIso();
    let earliest = null, coversToday = false;
    for (const p of visible) {
      const w = projFootprint(p);
      if (today >= w.start && today <= w.end) coversToday = true;
      if (!earliest || w.start < earliest) earliest = w.start;
    }
    if (!coversToday && earliest) setAvailDate(earliest);
  }, [visible]);

  // Timeline window. 'span' (default) = oldest project start → farthest project
  // end across what's shown (the real book of work), padded a week each side;
  // the Include-scenarios toggle controls whether far-out bids stretch it.
  // 'rolling' = fixed calendar window, 1st of last month through +12 months.
  const window_ = useMemo(() => {
    if (viewMode === 'rolling') return { start: monthStartIso(-1), end: monthStartIso(12) };
    if (!visible.length) return { start: todayIso(), end: addDays(todayIso(), 90) };
    let lo = null, hi = null;
    for (const p of visible) {
      const w = projFootprint(p);
      if (!lo || w.start < lo) lo = w.start;
      if (!hi || w.end > hi) hi = w.end;
    }
    return { start: addDays(lo, -7), end: addDays(hi, 7) };
  }, [visible, viewMode]);
  const totalDays = Math.max(daysBetween(window_.start, window_.end), 1);

  // Rows in chronological order so the master schedule reads top-to-bottom by
  // start date; off-window jobs (mostly far-future bids) sort to the bottom.
  const rows = useMemo(() => {
    const arr = [...visible];
    arr.sort((a, b) => {
      const sa = projFootprint(a).start, sb = projFootprint(b).start;
      return sa < sb ? -1 : sa > sb ? 1 : 0;
    });
    return arr;
  }, [visible]);

  // Month tick marks across the window.
  const ticks = useMemo(() => {
    const out = [];
    let d = toUTC(window_.start);
    d.setUTCDate(1);
    if (isoOf(d) < window_.start) d.setUTCMonth(d.getUTCMonth() + 1);
    while (isoOf(d) <= window_.end) {
      // "Nov '26" — apostrophe-year so a month tick can't be misread as a day (Nov 26th).
      out.push({ iso: isoOf(d), label: `${d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })} '${String(d.getUTCFullYear()).slice(-2)}` });
      d.setUTCMonth(d.getUTCMonth() + 1);
    }
    return out;
  }, [window_]);

  const stockByKey = useMemo(() => {
    const m = new Map();
    for (const it of stock?.items || []) m.set(it.cat_class, it);
    return m;
  }, [stock]);

  // Size-family index: masks/PPE come in sizes, written as a trailing
  // parenthesized token in the RM description — "AV3000 MASK (LARGE)". When
  // you're short on one size, the sibling sizes' availability is what tells
  // you whether it's a real shortage. Group cat-classes by base description
  // (size token stripped); keep families with 2+ members. cat_class -> the
  // OTHER sizes in its family, each with @800 / company availability.
  const sizeAlts = useMemo(() => {
    const SIZE_WORD = '(?:X-?SMALL|XX-?LARGE|X-?LARGE|SMALL|MEDIUM|LARGE|MED|SM|LG|XXL|XL|XS|S|M|L)';
    const RE = new RegExp(`\\(\\s*(${SIZE_WORD}(?:\\s*/\\s*${SIZE_WORD})*)\\s*\\)\\s*$`, 'i');
    const byBase = new Map();
    for (const [cat, st] of stockByKey.entries()) {
      const desc = st.description || '';
      const m = desc.match(RE);
      if (!m) continue;
      const size = m[1].toUpperCase().replace(/\s+/g, '');
      const base = desc.slice(0, m.index).trim().toUpperCase().replace(/\s+/g, ' ');
      if (!base) continue;
      if (!byBase.has(base)) byBase.set(base, []);
      byBase.get(base).push({
        cat_class: cat, size, description: desc,
        avail800: st.avail_by_branch?.['800'] || 0,
        availCo: st.available ?? null,
        avail_by_branch: st.avail_by_branch || null,
      });
    }
    const map = new Map();
    for (const members of byBase.values()) {
      if (members.length < 2) continue;   // a lone item isn't an "alternate"
      for (const me of members) {
        map.set(me.cat_class, members
          .filter(x => x.cat_class !== me.cat_class)
          .sort((a, b) => b.avail800 - a.avail800));
      }
    }
    return map;
  }, [stockByKey]);

  // Demand per CAT-CLASS across visible projects; lines without a CAT-CLASS
  // can't be matched to RM stock and fall into the unmatched bucket.
  const demand = useMemo(() => {
    const byKey = new Map();
    const unmatched = [];
    for (const p of visible) {
      const fp = projFootprint(p);
      for (const line of p.equipment_lines) {
        if (!line.qty) continue;
        // On-site window: owned equipment spans the labor footprint (first labor
        // in → last labor out), NOT num_days — num_days is the billed rental-day
        // count and can overshoot the actual job. Third-party rentals keep their
        // own rental window. Mirrors the availability memos.
        const w = line.equip_type === 'Third-Party'
          ? lineWindow(line, p)
          : { start: line.start_date || fp.start, end: bufEnd(line.end_date || fp.end) };
        if (line.cat_class && line.cat_class !== 'N/A') {
          let d = byKey.get(line.cat_class);
          if (!d) { d = { cat_class: line.cat_class, description: line.description, intervals: [], scenarioOnly: true }; byKey.set(line.cat_class, d); }
          d.intervals.push({ ...w, qty: line.qty });
          if (!p.scenario) d.scenarioOnly = false;
        } else if (line.equip_type !== 'Third-Party') {
          // Third-party rentals aren't ours — they can't (and shouldn't) be
          // checked against RM stock, and they already surface in the
          // Third-Party demand tables by description. Only OWNED lines
          // missing a CAT-CLASS are a real data problem worth warning about.
          unmatched.push({ project: p, line });
        }
      }
    }
    const rows = [...byKey.values()].map(d => {
      const peak = peakOf(d.intervals);
      const st = stockByKey.get(d.cat_class);
      const owned = st ? st.owned : null;
      return {
        cat_class: d.cat_class,
        description: st?.description || d.description,
        peak,
        owned,
        available: st ? st.available : null,
        on_ta: st ? (st.on_ta || 0) : null,
        on_rent: st ? st.on_rent : null,
        avail_by_branch: st ? (st.avail_by_branch || {}) : null,
        shortage: owned != null ? Math.max(0, peak - owned) : null,
        scenarioOnly: d.scenarioOnly
      };
    }).sort((a, b) => (b.shortage || 0) - (a.shortage || 0) || a.cat_class.localeCompare(b.cat_class, undefined, { numeric: true }));
    return { rows, unmatched };
  }, [visible, stockByKey]);

  // Date-phased availability (the "Equipment Availability" balance): for the
  // picked date, per CAT-CLASS show owned fleet vs how many are deployed
  // (accounted for) by jobs on-site that day, and the surplus/shortage.
  // Equipment on-site window = the project's labor span (first labor in → last
  // labor out, George 2026-06-13) unless the line carries its own dates.
  const availability = useMemo(() => {
    const fleet = new Map();      // cat_class -> qty deployed (owned equipment) on availDate
    const descByCat = new Map();
    const thirdParty = new Map(); // third-party WITH rental days, on availDate
    const tpConfirm = new Map();  // third-party with qty but NO rental days — confirm, don't auto-rent
    for (const p of visible) {
      const fp = projFootprint(p);  // first labor in → last labor out (pre-TA mob → post-TA demob)
      for (const line of (p.equipment_lines || [])) {
        const qty = Number(line.qty) || 0;
        if (!qty) continue;
        if (line.equip_type === 'Third-Party') {
          const realCat = line.cat_class && line.cat_class !== 'N/A' ? line.cat_class : null;
          const st = realCat ? stockByKey.get(realCat) : null;
          const label = st?.description || line.description || realCat || '—';
          const key = `${realCat || ''}|${line.description || ''}`;
          const days = Number(line.num_days) || 0;
          const hasDays = days > 0 || (line.start_date && line.end_date);
          if (!hasDays) {  // qty but no rental days — flag for confirmation, never auto-rent
            let t = tpConfirm.get(key);
            if (!t) { t = { label, cat_class: realCat, qty: 0, jobs: new Set() }; tpConfirm.set(key, t); }
            t.qty += qty; t.jobs.add(p.job_number || p.site);
            continue;
          }
          const s = line.start_date || fp.start;
          let e = line.end_date || (days > 0 ? addDays(s, days - 1) : fp.end);
          if (availDate < s || availDate > e) continue;
          let t = thirdParty.get(key);
          if (!t) { t = { label, cat_class: realCat, qty: 0, jobs: new Set() }; thirdParty.set(key, t); }
          t.qty += qty; t.jobs.add(p.job_number || p.site);
        } else if (line.cat_class && line.cat_class !== 'N/A') {
          // Owned equipment is on-site the whole footprint (NOT num_days),
          // then occupied RETURN_BUFFER_DAYS more for transport + flip.
          const s = line.start_date || fp.start;
          const e = bufEnd(line.end_date || fp.end);
          if (availDate < s || availDate > e) continue;
          fleet.set(line.cat_class, (fleet.get(line.cat_class) || 0) + qty);
          if (!descByCat.has(line.cat_class)) descByCat.set(line.cat_class, line.description);
        }
      }
    }
    const fleetRows = [...fleet.entries()].map(([cat, dep]) => {
      const st = stockByKey.get(cat);
      const avail800 = st?.avail_by_branch?.['800'] || 0;   // TA-division yard
      const availCo = st ? st.available : null;             // parent-branch companywide
      const net800 = avail800 - dep;
      const netCo = availCo == null ? null : availCo - dep;
      const shortCo = netCo != null && netCo < 0 ? -netCo : 0;   // nobody has → re-rent
      const short800 = net800 < 0 ? -net800 : 0;
      const transferIn = Math.max(0, short800 - shortCo);        // other branches can send
      const status = availCo == null ? 'NO STOCK' : shortCo > 0 ? 'RE-RENT' : short800 > 0 ? 'TRANSFER-IN' : 'OK';
      return {
        cat_class: cat, description: st?.description || descByCat.get(cat) || '',
        avail800, availCo, deployed: dep, net800, netCo, transferIn, reRent: shortCo, status,
        avail_by_branch: st?.avail_by_branch || null,   // for the click-through location modal
      };
    }).sort((a, b) => (a.net800 ?? 1e9) - (b.net800 ?? 1e9)); // worst 800 position first
    const thirdPartyRows = [...thirdParty.values()].map(t => ({ ...t, jobs: [...t.jobs] })).sort((a, b) => b.qty - a.qty);
    const tpConfirmRows = [...tpConfirm.values()].map(t => ({ ...t, jobs: [...t.jobs] })).sort((a, b) => b.qty - a.qty);
    return { fleetRows, thirdPartyRows, tpConfirmRows };
  }, [visible, stockByKey, availDate]);

  // WHOLE-TIMELINE availability: per owned-fleet cat_class, the availability
  // (owned − deployed) across the entire window as stepped segments — green
  // surplus, red shortage — so every over-allocated date range is visible at
  // once, no date scrubbing. Deployed steps at project footprint breakpoints.
  const availTimeline = useMemo(() => {
    const byCat = new Map();
    for (const p of visible) {
      const fp = projFootprint(p);
      for (const line of (p.equipment_lines || [])) {
        const qty = Number(line.qty) || 0;
        if (!qty || line.equip_type === 'Third-Party') continue;
        const cat = line.cat_class;
        if (!cat || cat === 'N/A') continue;
        // Owned equipment is on-site the whole footprint (NOT num_days),
        // then occupied RETURN_BUFFER_DAYS more for transport + flip.
        const s = line.start_date || fp.start;
        const e = bufEnd(line.end_date || fp.end);
        let d = byCat.get(cat);
        if (!d) {
          const st = stockByKey.get(cat);
          d = { cat_class: cat, description: st?.description || line.description || '',
            avail800: st?.avail_by_branch?.['800'] || 0, availCo: st ? st.available : null, intervals: [] };
          byCat.set(cat, d);
        }
        d.intervals.push({ start: s, end: e, qty });
      }
    }
    const out = [];
    for (const d of byCat.values()) {
      const bounds = [...new Set(d.intervals.flatMap(i => [i.start, addDays(i.end, 1)]))].sort();
      const segs = [];
      let worstNet800 = null, peakDeployed = 0;
      for (let i = 0; i < bounds.length - 1; i++) {
        const a = bounds[i], b = bounds[i + 1];
        const dep = d.intervals.filter(iv => iv.start <= a && addDays(iv.end, 1) > a).reduce((s, iv) => s + iv.qty, 0);
        if (dep === 0) continue;
        if (dep > peakDeployed) peakDeployed = dep;
        const net800 = d.avail800 - dep;
        const netCo = d.availCo == null ? null : d.availCo - dep;
        // green = covered at 800; amber = short at 800 but company has it (transfer); red = company short (re-rent)
        const tier = d.availCo == null ? 'none' : (netCo < 0 ? 'rent' : net800 < 0 ? 'transfer' : 'ok');
        if (worstNet800 == null || net800 < worstNet800) worstNet800 = net800;
        segs.push({ start: a, end: b, deployed: dep, net800, netCo, tier });
      }
      out.push({ ...d, segs, worstNet800, peakDeployed });
    }
    out.sort((x, y) => {
      const mx = x.worstNet800 == null ? 1e9 : x.worstNet800, my = y.worstNet800 == null ? 1e9 : y.worstNet800;
      return mx - my || x.cat_class.localeCompare(y.cat_class, undefined, { numeric: true });
    });
    return out;
  }, [visible, stockByKey]);

  // Manpower demand over time: per craft (the Day/Night split is already baked
  // into the craft name), the concurrent headcount across the window as stepped
  // segments — so you see WHEN each crew is needed, not just the peak. Inclusive
  // end-date convention (end_date = last day on-site), matching the per-project
  // Daily Labor Schedule so the peak, the timeline, and that view all agree.
  const laborDemand = useMemo(() => {
    const byCraft = new Map();
    for (const p of visible) {
      for (const line of (p.labor_lines || [])) {
        const hc = (Number(line.day_hc) || 0) + (Number(line.night_hc) || 0);
        if (!hc) continue;
        const w = lineWindow(line, p);
        let d = byCraft.get(line.craft);
        if (!d) { d = { craft: line.craft, intervals: [] }; byCraft.set(line.craft, d); }
        d.intervals.push({ start: w.start, end: w.end, qty: hc });
      }
    }
    const out = [];
    for (const d of byCraft.values()) {
      const bounds = [...new Set(d.intervals.flatMap(i => [i.start, addDays(i.end, 1)]))].sort();
      const segs = [];
      let peak = 0, start = null, end = null;
      for (let i = 0; i < bounds.length - 1; i++) {
        const a = bounds[i], b = bounds[i + 1];
        const hc = d.intervals.filter(iv => iv.start <= a && addDays(iv.end, 1) > a).reduce((s, iv) => s + iv.qty, 0);
        if (hc === 0) continue;          // gap between phases — leave it blank
        if (hc > peak) peak = hc;
        if (start == null) start = a;
        end = b;
        segs.push({ start: a, end: b, hc });
      }
      out.push({ craft: d.craft, segs, peak, start, end });
    }
    return out.sort((a, b) => b.peak - a.peak || a.craft.localeCompare(b.craft, undefined, { numeric: true }));
  }, [visible]);
  const laborPeak = useMemo(() => Math.max(1, ...laborDemand.map(r => r.peak)), [laborDemand]);

  // Per-header sort + filter controls (the field-services table pattern) for the
  // analysis tables, so every header sorts and filters. Render *Ctl.displayRows.
  const demandCtl = useTableControls(demand.rows, {
    cat_class: r => r.cat_class, description: r => r.description,
    peak: r => r.peak, owned: r => r.owned, available: r => r.available,
    on_ta: r => r.on_ta, on_rent: r => r.on_rent, shortage: r => r.shortage,
  });
  const availCtl = useTableControls(availability.fleetRows, {
    cat_class: r => r.cat_class, description: r => r.description,
    deployed: r => r.deployed, avail800: r => r.avail800, availCo: r => r.availCo,
    net800: r => r.net800, status: r => r.status,
  });
  const laborCtl = useTableControls(laborDemand, {
    craft: r => r.craft, peak: r => r.peak, start: r => r.start,
  });

  const onToggleOff = async (p) => {
    setBusy(true);
    setError('');
    try { await api.ganttToggle(p.id, false); await reload(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const onUploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setError('');
    setUploadResult(null);
    try {
      const result = await api.ganttUpload(file);
      setUploadResult(result);
      await reload();
    } catch (e) { setError(e.message); }
    finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {stock ? (
            <>
              <FreshnessChip lastModified={stock.report_last_modified} stale={stock.stale} />
              <span>as of {fmtDateTimeShort(stock.report_last_modified)} · Rental Man, companywide · scenario bids are ghosted and never reach the warehouse.</span>
            </>
          ) : (
            <span style={{ color: '#B45309' }}>Stock counts unavailable: {stockError || 'loading…'}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
            title="Book of work: spans the oldest project start to the farthest project end shown (Include scenarios stretches it to far-out bids). Rolling 12 months: fixed calendar window, 1st of last month through +12 months.">
            View
            <select style={{ ...styles.input, width: 'auto', fontSize: 12, padding: '4px 8px' }}
              value={viewMode} onChange={e => setViewMode(e.target.value)}>
              <option value="span">Book of work (active → pending)</option>
              <option value="rolling">Rolling 12 months</option>
            </select>
          </label>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
            title="Focus the schedule and every analysis card on one job, or All jobs for the master roll-up. Use one job to see when YOU need crews/equipment for that turnaround; the roll-up mixes all jobs on the Gantt.">
            Focus
            <select style={{ ...styles.input, width: 'auto', fontSize: 12, padding: '4px 8px', maxWidth: 260 }}
              value={onGantt.some(p => p.id === focusId) ? focusId : ''}
              onChange={e => setFocusId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">All jobs ({onGantt.length})</option>
              {onGantt.map(p => (
                <option key={p.id} value={p.id}>{p.job_number || p.site || `Job ${p.id}`}{p.scenario ? ' (scenario)' : ''}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeScenarios} onChange={e => setIncludeScenarios(e.target.checked)} />
            Include scenarios
          </label>
          <button style={styles.btnSecondary} disabled={busy}
            onClick={() => downloadBlob(api.ganttTemplate(), 'gantt-jobs-template.xlsx').catch(e => setError(e.message))}>
            ⬇ Job Template
          </button>
          <DropZone onFiles={files => onUploadFile(files[0])} disabled={busy} title="Drop the jobs template file here">
            <button style={styles.btnSecondary} disabled={busy} onClick={() => fileRef.current?.click()}>
              ⬆ Upload Jobs
            </button>
          </DropZone>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
            onChange={e => onUploadFile(e.target.files?.[0])} />
          <button style={styles.btnSecondary} disabled={busy || mgBusy}
            title="Multi-project roll-up Gantt (Jobs / By Job / Fleet Demand), scoped to the current Focus + Include-scenarios selection. Quantity-only — no dollar figures."
            onClick={() => {
              setMgBusy(true);
              downloadBlob(api.ganttMasterGantt(focusId, includeScenarios), `Master-Gantt-${focusId != null && onGantt.some(p => p.id === focusId) ? 'Focused' : 'AllJobs'}-${todayIso()}.xlsx`)
                .catch(e => setError(e.message))
                .finally(() => setMgBusy(false));
            }}>
            {mgBusy ? 'Exporting…' : '⬇ Master Gantt (xlsx)'}
          </button>
          <button style={styles.btnSecondary} disabled={busy} onClick={reload}>↻ Refresh</button>
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}
      {uploadResult && (
        <div style={{ ...styles.card, background: '#ECFDF5', borderColor: '#10B981', fontSize: 13 }}>
          Upload: {uploadResult.jobs_created} job(s) created, {uploadResult.jobs_updated} updated,
          {' '}{uploadResult.equipment_lines} equipment line(s), {uploadResult.labor_lines} labor line(s).
          {uploadResult.errors?.length > 0 && (
            <ul style={{ margin: '6px 0 0 18px', color: '#B45309' }}>
              {uploadResult.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}

      <AddProjectPicker busy={busy} onAdded={reload} setError={setError} />

      {/* ---- Timeline ---- */}
      <div style={styles.card}>
        <RollupH2 collapsed={rolledSched} onToggle={toggleSched} style={{ marginBottom: rolledSched ? 0 : undefined }}>Schedule <span style={{ color: '#6B7280', fontWeight: 400 }}>· {focusId != null && visible.length === 1 ? `focused: ${visible[0].job_number || visible[0].site}` : `${visible.length} job(s)`}</span></RollupH2>
        {!rolledSched && <>
        {visible.length === 0 && (
          <div style={{ color: '#6B7280', fontSize: 13, padding: '12px 0' }}>
            Nothing on the Gantt yet. Add a job above — Won/Active jobs render solid, bids render as ghosted scenarios.
          </div>
        )}
        {visible.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 760 }}>
              {/* month ruler */}
              <div style={{ position: 'relative', height: 22, marginLeft: LABEL_W, borderBottom: '1px solid #E5E7EB' }}>
                {ticks.map(t => (
                  <div key={t.iso} style={{
                    position: 'absolute', left: pct(t.iso, window_, totalDays), top: 0, fontSize: 11,
                    color: '#6B7280', borderLeft: '1px solid #E5E7EB', paddingLeft: 4, height: '100%'
                  }}>{t.label}</div>
                ))}
              </div>
              {rows.map(p => {
                const w = projFootprint(p);  // bar spans pre-TA mob → post-TA demob, not just mechanical
                // Clamp the bar to the visible window so off-window jobs don't
                // overflow; fully-out jobs show a faint edge marker instead.
                const vStart = w.start > window_.start ? w.start : window_.start;
                const vEnd = w.end < window_.end ? w.end : window_.end;
                const inWindow = daysBetween(vStart, vEnd) > 0;
                const offFuture = w.start >= window_.end;
                const left = pct(vStart, window_, totalDays);
                const width = `${Math.min(Math.max((daysBetween(vStart, vEnd) / totalDays) * 100, 0.5), 100)}%`;
                return (
                  <div key={p.id}>
                    <div style={{ display: 'flex', alignItems: 'center', minHeight: 34, borderBottom: '1px solid #F3F4F6' }}>
                      <div style={{ width: LABEL_W, flexShrink: 0, fontSize: 12, paddingRight: 8, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                        <button
                          title={expandedId === p.id ? 'Collapse' : 'Detail / punch list'}
                          style={{ ...styles.btnSecondary, padding: '1px 7px', fontSize: 11 }}
                          onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                        >{expandedId === p.id ? '▾' : '▸'}</button>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <strong>{p.job_number || '—'}</strong> {p.site}
                          {p.scenario && <span style={scenarioBadge}>SCENARIO</span>}
                        </span>
                      </div>
                      <div style={{ position: 'relative', flexGrow: 1, height: 18 }}>
                        {inWindow ? (
                          <div
                            title={`${p.site}: ${fmtDate(w.start)} → ${fmtDate(w.end)} (${p.status})`}
                            style={{
                              position: 'absolute', left, width, height: 18, borderRadius: 4,
                              background: p.scenario ? 'transparent' : colors.orange,
                              border: `2px ${p.scenario ? 'dashed' : 'solid'} ${p.scenario ? '#9CA3AF' : colors.orange}`,
                              opacity: p.scenario ? 0.8 : 1
                            }}
                          />
                        ) : (
                          <div title={`${p.site}: ${fmtDate(w.start)} → ${fmtDate(w.end)} — outside the rolling window`}
                            style={{ position: 'absolute', left: offFuture ? 'auto' : 0, right: offFuture ? 0 : 'auto',
                              top: 3, fontSize: 11, color: '#9CA3AF', fontWeight: 700 }}>
                            {offFuture ? `${fmtDate(w.start)} →` : `← ${fmtDate(w.end)}`}
                          </div>
                        )}
                      </div>
                    </div>
                    {expandedId === p.id && (
                      <ProjectDetail project={p} busy={busy} setError={setError}
                        onToggleOff={() => onToggleOff(p)} onChanged={reload} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </>}
      </div>

      {/* ---- Whole-window availability timeline ---- */}
      <div style={styles.card}>
        <RollupH2 collapsed={rolledTimeline} onToggle={toggleTimeline} style={{ marginBottom: rolledTimeline ? 0 : undefined }}>Equipment Availability — full timeline</RollupH2>
        {!rolledTimeline && <>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
          One row per owned item across the whole window. The number in each segment = units <strong>available at the 800 yard</strong> after that span's demand.{' '}
          <strong style={{ color: '#16A34A' }}>Green</strong> = covered at 800, <strong style={{ color: '#B45309' }}>amber</strong> = short at 800 but the company has it (transfer in), <strong style={{ color: '#B91C1C' }}>red</strong> = company short (re-rent), <strong style={{ color: '#6B7280' }}>gray</strong> = no RM stock record for this CAT-CLASS. Worst 800 shortages first. Equipment spans pre-TA mob → post-TA demob.
        </div>
        {availTimeline.length === 0 ? (
          <div style={{ fontSize: 13, color: '#6B7280' }}>No owned-fleet equipment on the Gantt.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 760 }}>
              <div style={{ position: 'relative', height: 18, marginLeft: LABEL_W, borderBottom: '1px solid #E5E7EB' }}>
                {ticks.map(t => (
                  <div key={t.iso} style={{ position: 'absolute', left: pct(t.iso, window_, totalDays), top: 0, fontSize: 10, color: '#6B7280', borderLeft: '1px solid #E5E7EB', paddingLeft: 3, height: '100%' }}>{t.label}</div>
                ))}
              </div>
              {availTimeline.map(r => (
                <div key={r.cat_class} style={{ display: 'flex', alignItems: 'center', minHeight: 24, borderBottom: '1px solid #F8FAFC' }}>
                  <div style={{ width: LABEL_W, flexShrink: 0, fontSize: 11, paddingRight: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={`${r.cat_class} ${r.description} — 800 avail ${r.avail800}, company avail ${r.availCo ?? '—'}, peak deployed ${r.peakDeployed}`}>
                    <span style={{ fontWeight: 700 }}>{r.cat_class}</span> {r.description}
                    <span style={{ color: '#9CA3AF' }}> · 800:{r.avail800} · co:{r.availCo ?? '—'}</span>
                    {r.worstNet800 != null && r.worstNet800 < 0 && <span style={{ color: '#B91C1C', fontWeight: 700 }}> · short {-r.worstNet800} @800</span>}
                  </div>
                  <div style={{ position: 'relative', flexGrow: 1, height: 18 }}>
                    {r.segs.map((s, i) => {
                      const left = pct(s.start, window_, totalDays);
                      const width = `${Math.max(daysBetween(s.start, s.end) / totalDays * 100, 0.5)}%`;
                      const bg = s.tier === 'none' ? '#9CA3AF' : s.tier === 'rent' ? '#DC2626' : s.tier === 'transfer' ? '#F59E0B' : '#16A34A';
                      const co = s.netCo == null ? '' : `, company ${s.netCo}${s.netCo < 0 ? ' (re-rent)' : ''}`;
                      return <div key={i}
                        title={`${fmtDate(s.start)} → ${fmtDate(addDays(s.end, -1))}: ${s.deployed} deployed · 800 net ${s.net800}${s.net800 < 0 && s.tier === 'transfer' ? ' (transfer in)' : ''}${co}`}
                        style={{ position: 'absolute', left, width, height: 18, background: bg, opacity: s.tier === 'ok' ? 0.85 : 1, color: '#fff', fontSize: 9, fontWeight: 700, textAlign: 'center', lineHeight: '18px', overflow: 'hidden', borderRight: '1px solid #fff' }}>
                        {s.net800}
                      </div>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </>}
      </div>

      {/* ---- Date-phased availability balance ---- */}
      <div style={styles.card}>
        <RollupH2 collapsed={rolledDate} onToggle={toggleDate} style={{ marginBottom: rolledDate ? 0 : undefined }}>Equipment Availability on a Date <span style={{ fontWeight: 400, fontSize: 12, color: '#6B7280' }}>· exact counts for one day</span></RollupH2>
        {!rolledDate && <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
            On date
            <input type="date" value={availDate}
              onChange={e => { dateTouchedRef.current = true; setAvailDate(e.target.value || todayIso()); }}
              style={{ ...styles.input, width: 'auto', fontSize: 13 }} />
          </label>
          <span style={{ fontSize: 12, color: '#6B7280' }}>
            Owned fleet vs what's deployed on this date across the jobs shown.{' '}
            <strong style={{ color: '#B45309' }}>TRANSFER-IN</strong> = short at the 800 yard but the company has it (request a transfer); <strong style={{ color: '#B91C1C' }}>RE-RENT</strong> = company short (third-party).
          </span>
        </div>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>
          Available = RentalMan "Available" units (not on rent / not on a TA). <strong>@800</strong> = the TA-division yard; <strong>Company</strong> = all parent branches (100–800). Deployed only counts jobs flipped onto the Gantt above — flip every live job on for a true balance. Owned equipment stays "deployed" for <strong>{RETURN_BUFFER_DAYS} days after last labor out</strong> (transport + rent-ready flip).
        </div>
        <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', margin: '4px 0' }}>Owned fleet</div>
        <ActiveFiltersBar filters={availCtl.filters} clearFilter={availCtl.clearFilter} clearAll={availCtl.clearAll}
          colLabels={{ cat_class: 'CAT-CLASS', description: 'Description', deployed: 'Deployed', avail800: 'Avail @800', availCo: 'Avail Co.', net800: 'Net @800', status: 'Status' }} />
        <table style={styles.table}>
          <thead><tr>
            <SortableFilterTh {...availCtl.headerProps('cat_class')} label="CAT-CLASS" />
            <SortableFilterTh {...availCtl.headerProps('description')} label="Description" />
            <SortableFilterTh {...availCtl.headerProps('deployed')} label="Deployed" align="right" />
            <SortableFilterTh {...availCtl.headerProps('avail800')} label="Avail @800" align="right" />
            <SortableFilterTh {...availCtl.headerProps('availCo')} label="Avail Co." align="right" />
            <SortableFilterTh {...availCtl.headerProps('net800')} label="Net @800" align="right" />
            <SortableFilterTh {...availCtl.headerProps('status')} label="Status" align="center" />
          </tr></thead>
          <tbody>
            {availCtl.displayRows.length === 0 && (
              <tr><td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>No owned-fleet equipment deployed on {fmtDate(availDate)}.</td></tr>
            )}
            {availCtl.displayRows.map(r => {
              const bg = r.status === 'RE-RENT' ? '#FEF2F2' : r.status === 'TRANSFER-IN' ? '#FFFBEB' : undefined;
              const badge = r.status === 'OK' ? { t: 'OK', b: '#D1FAE5', c: '#065F46' }
                : r.status === 'TRANSFER-IN' ? { t: `TRANSFER-IN ${r.transferIn}`, b: '#FEF3C7', c: '#92400E' }
                : r.status === 'RE-RENT' ? { t: `RE-RENT ${r.reRent}${r.transferIn > 0 ? ` · TRANSFER ${r.transferIn}` : ''}`, b: '#FEE2E2', c: '#991B1B' }
                : { t: 'NO STOCK', b: '#F3F4F6', c: '#6B7280' };
              return (
              <tr key={r.cat_class} style={bg ? { background: bg } : undefined}>
                <td style={styles.td}>{r.cat_class}</td>
                <td style={styles.td}>
                  {r.description}
                  {sizeAlts.get(r.cat_class)?.length > 0 && (
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                      <span style={{ color: '#9CA3AF' }}>Other sizes:</span>{' '}
                      {sizeAlts.get(r.cat_class).map((alt, i) => (
                        <span key={alt.cat_class}>
                          {i > 0 && ' · '}
                          <button type="button"
                            title={`${alt.description} (${alt.cat_class}) — ${alt.avail800} available @800, ${alt.availCo ?? '—'} companywide. Click for branch locations.`}
                            onClick={() => alt.avail_by_branch && Object.keys(alt.avail_by_branch).length > 0
                              && setLocDetail({ cat_class: alt.cat_class, description: alt.description, avail_by_branch: alt.avail_by_branch })}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: alt.avail_by_branch ? 'pointer' : 'default',
                              fontSize: 'inherit', fontWeight: 600, textDecoration: alt.avail_by_branch ? 'underline' : 'none',
                              color: alt.avail800 > 0 ? '#065F46' : '#9CA3AF' }}>
                            {alt.size} {alt.avail800}@800
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{r.deployed}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{r.avail800}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{r.availCo ?? '—'}</td>
                <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>
                  {r.avail_by_branch && Object.keys(r.avail_by_branch).length > 0 ? (
                    <button type="button"
                      title="Click to see where these units sit across the company — what's allocatable at other branches to transfer in"
                      onClick={() => setLocDetail({ cat_class: r.cat_class, description: r.description, avail_by_branch: r.avail_by_branch })}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit', fontWeight: 700, color: r.net800 < 0 ? '#B91C1C' : colors.green }}>
                      {r.net800}
                    </button>
                  ) : (
                    <span style={{ color: r.net800 < 0 ? '#B91C1C' : colors.green }}>{r.net800}</span>
                  )}
                </td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: badge.b, color: badge.c }}>{badge.t}</span>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {/* Third-party items: not in the fleet — must be rented from a vendor. */}
        <div style={{ fontWeight: 600, fontSize: 12, color: '#B45309', margin: '14px 0 4px' }}>
          Third-party — prepare to rent on {fmtDate(availDate)} <span style={{ fontWeight: 400, color: '#6B7280' }}>(not in your inventory, has rental days)</span>
        </div>
        {availability.thirdPartyRows.length === 0 ? (
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>No third-party rentals on this date.</div>
        ) : (
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Item</th>
              <th style={styles.th}>CAT-CLASS</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Qty to rent</th>
              <th style={styles.th}>Job(s)</th>
            </tr></thead>
            <tbody>
              {availability.thirdPartyRows.map((t, i) => (
                <tr key={i} style={{ background: '#FFFBEB' }}>
                  <td style={styles.td}>{t.label}</td>
                  <td style={styles.td}>{t.cat_class || <span style={{ color: '#9CA3AF' }}>—</span>}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, color: '#B45309' }}>{t.qty}</td>
                  <td style={{ ...styles.td, fontSize: 11, color: '#6B7280' }}>{t.jobs.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Third-party lines with qty but NO rental days — not auto-rented; confirm. */}
        {availability.tpConfirmRows.length > 0 && (
          <>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#6B7280', margin: '14px 0 4px' }}>
              Third-party — no rental days set <span style={{ fontWeight: 400 }}>(qty entered but 0 days — confirm whether these are actually needed; not counted as a rental)</span>
            </div>
            <table style={styles.table}>
              <thead><tr>
                <th style={styles.th}>Item</th>
                <th style={styles.th}>CAT-CLASS</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Qty</th>
                <th style={styles.th}>Job(s)</th>
              </tr></thead>
              <tbody>
                {availability.tpConfirmRows.map((t, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{t.label}</td>
                    <td style={styles.td}>{t.cat_class || <span style={{ color: '#9CA3AF' }}>—</span>}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{t.qty}</td>
                    <td style={{ ...styles.td, fontSize: 11, color: '#6B7280' }}>{t.jobs.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        </>}
      </div>

      {/* ---- Equipment demand vs stock (peak across the whole window) ---- */}
      <div style={styles.card}>
        <RollupH2 collapsed={rolledDemand} onToggle={toggleDemand} style={{ marginBottom: rolledDemand ? 0 : undefined }}>Equipment Demand vs Owned Stock <span style={{ fontWeight: 400, fontSize: 12, color: '#6B7280' }}>· peak across the whole window</span></RollupH2>
        {!rolledDemand && <>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
          Peak = highest concurrent quantity needed across the jobs shown. Owned excludes Junked / Sold / Missing / Theft.
          {' '}<strong style={{ color: '#B91C1C' }}>Re-rent</strong> = peak demand above what SSS owns companywide — the quantity to price into the quote.
        </div>
        <ActiveFiltersBar filters={demandCtl.filters} clearFilter={demandCtl.clearFilter} clearAll={demandCtl.clearAll}
          colLabels={{ cat_class: 'CAT-CLASS', description: 'Description', peak: 'Peak Need', owned: 'Owned', available: 'Available Now', on_ta: 'On TA', on_rent: 'On Rent', shortage: 'Re-rent' }} />
        <table style={styles.table}>
          <thead>
            <tr>
              <SortableFilterTh {...demandCtl.headerProps('cat_class')} label="CAT-CLASS" />
              <SortableFilterTh {...demandCtl.headerProps('description')} label="Description" />
              <SortableFilterTh {...demandCtl.headerProps('peak')} label="Peak Need" align="right" />
              <SortableFilterTh {...demandCtl.headerProps('owned')} label="Owned" align="right" />
              <SortableFilterTh {...demandCtl.headerProps('available')} label="Available Now" align="right" />
              <SortableFilterTh {...demandCtl.headerProps('on_ta')} label="On TA" align="right" />
              <SortableFilterTh {...demandCtl.headerProps('on_rent')} label="On Rent" align="right" />
              <SortableFilterTh {...demandCtl.headerProps('shortage')} label="Re-rent" align="right" />
            </tr>
          </thead>
          <tbody>
            {demandCtl.displayRows.length === 0 && (
              <tr><td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>No matched equipment demand on the Gantt.</td></tr>
            )}
            {demandCtl.displayRows.map(r => (
              <tr key={r.cat_class} style={r.shortage > 0 ? { background: '#FEF2F2' } : undefined}>
                <td style={styles.td}>{r.cat_class}{r.scenarioOnly && <span style={scenarioBadge}>SCEN</span>}</td>
                <td style={styles.td}>{r.description}</td>
                <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{r.peak}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{r.owned ?? '—'}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  {r.available == null ? '—'
                    : r.avail_by_branch && Object.keys(r.avail_by_branch).length > 0 ? (
                      <button type="button"
                        title="Click to see where these units are located"
                        onClick={() => setLocDetail({ cat_class: r.cat_class, description: r.description, avail_by_branch: r.avail_by_branch })}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1D4ED8', textDecoration: 'underline', fontSize: 'inherit', fontWeight: 600 }}>
                        {r.available}
                      </button>
                    ) : r.available}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: r.on_ta > 0 ? '#B45309' : undefined }}>{r.on_ta ?? '—'}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{r.on_rent ?? '—'}</td>
                <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, color: r.shortage > 0 ? '#B91C1C' : '#10B981' }}>
                  {r.shortage == null ? 'no stock data' : r.shortage > 0 ? r.shortage : 'OK'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {demand.unmatched.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#B45309' }}>
            <strong>{demand.unmatched.length} line(s) without a CAT-CLASS</strong> can't be checked against stock:
            {' '}{demand.unmatched.slice(0, 8).map((u, i) => (
              <span key={i}>{i > 0 && '; '}{u.line.description} ({u.line.qty}× — {u.project.job_number || u.project.site})</span>
            ))}{demand.unmatched.length > 8 && '; …'}
            {' '}Add the CAT-CLASS on the line (or in the equipment catalog) to include them.
          </div>
        )}
        </>}
      </div>

      {/* ---- Allocation worksheet: plan the pull by branch, then email ---- */}
      <AllocationWorksheet rows={demand.rows} projects={visible} allJobs={onGantt} focusId={focusId} onFocus={setFocusId} onShowLocations={setLocDetail} />

      {/* ---- Labor demand ---- */}
      <div style={styles.card}>
        <RollupH2 collapsed={rolledLabor} onToggle={toggleLabor} style={{ marginBottom: rolledLabor ? 0 : undefined }}>Manpower Demand <span style={{ fontWeight: 400, fontSize: 12, color: '#6B7280' }}>· peak headcount + when each craft is on-site</span></RollupH2>
        {!rolledLabor && <>
        {/* Job tabs — see all jobs combined, or scope the manpower demand to
            one job (George 2026-06-29). Drives the same page Focus as the worksheet. */}
        <JobTabs allJobs={onGantt} focusId={focusId} onFocus={setFocusId} />
        {laborDemand.length === 0 ? (
          <div style={{ fontSize: 13, color: '#6B7280' }}>No labor demand on the Gantt.</div>
        ) : (
          <>
            <ActiveFiltersBar filters={laborCtl.filters} clearFilter={laborCtl.clearFilter} clearAll={laborCtl.clearAll}
              colLabels={{ craft: 'Craft', peak: 'Peak Headcount', start: 'On-site window' }} />
            <table style={{ ...styles.table, maxWidth: 640 }}>
              <thead>
                <tr>
                  <SortableFilterTh {...laborCtl.headerProps('craft')} label="Craft" />
                  <SortableFilterTh {...laborCtl.headerProps('peak')} label="Peak Headcount" align="right" />
                  <SortableFilterTh {...laborCtl.headerProps('start')} label="On-site window" noFilter />
                </tr>
              </thead>
              <tbody>
                {laborCtl.displayRows.map(r => (
                  <tr key={r.craft}>
                    <td style={styles.td}>{r.craft}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{r.peak}</td>
                    <td style={{ ...styles.td, fontSize: 12, color: '#6B7280' }}>
                      {r.start ? `${fmtDate(r.start)} → ${fmtDate(addDays(r.end, -1))}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* When the crews are needed — stepped headcount per craft across the window. */}
            <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', margin: '14px 0 2px' }}>When — headcount over the schedule</div>
            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 6 }}>
              Number in each segment = people of that craft on-site during that span (darker = larger crew). Same window as the Gantt above; a gap means none needed then.
            </div>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 760 }}>
                <div style={{ position: 'relative', height: 18, marginLeft: LABEL_W, borderBottom: '1px solid #E5E7EB' }}>
                  {ticks.map(t => (
                    <div key={t.iso} style={{ position: 'absolute', left: pct(t.iso, window_, totalDays), top: 0, fontSize: 10, color: '#6B7280', borderLeft: '1px solid #E5E7EB', paddingLeft: 3, height: '100%' }}>{t.label}</div>
                  ))}
                </div>
                {laborDemand.map(r => (
                  <div key={r.craft} style={{ display: 'flex', alignItems: 'center', minHeight: 24, borderBottom: '1px solid #F8FAFC' }}>
                    <div style={{ width: LABEL_W, flexShrink: 0, fontSize: 11, paddingRight: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${r.craft} — peak ${r.peak}`}>
                      <span style={{ fontWeight: 700 }}>{r.craft}</span>
                      <span style={{ color: '#9CA3AF' }}> · peak {r.peak}</span>
                    </div>
                    <div style={{ position: 'relative', flexGrow: 1, height: 18 }}>
                      {r.segs.map((s, i) => {
                        const left = pct(s.start, window_, totalDays);
                        const width = `${Math.max(daysBetween(s.start, s.end) / totalDays * 100, 0.5)}%`;
                        return <div key={i}
                          title={`${fmtDate(s.start)} → ${fmtDate(addDays(s.end, -1))}: ${s.hc} on-site`}
                          style={{ position: 'absolute', left, width, height: 18, background: `rgba(230,111,44,${(0.3 + 0.6 * (s.hc / laborPeak)).toFixed(2)})`, borderRight: '1px solid #fff', textAlign: 'center', fontSize: 9, fontWeight: 700, color: '#1F2937', lineHeight: '18px', overflow: 'hidden' }}>
                          {s.hc}
                        </div>;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        </>}
      </div>

      {locDetail && <LocationModal detail={locDetail} onClose={() => setLocDetail(null)} />}
    </>
  );
}

// Where the available (and committed) units for a CAT-CLASS sit, by branch.
function LocationModal({ detail, onClose }) {
  const entries = Object.entries(detail.avail_by_branch || {})
    .map(([code, count]) => ({ code, count: Number(count) }))
    .sort((a, b) => b.count - a.count);
  const parents = entries.filter(e => isParentCode(e.code));
  const committed = entries.filter(e => !isParentCode(e.code));
  const sum = (arr) => arr.reduce((s, e) => s + e.count, 0);
  const Row = ({ e }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13, borderBottom: '1px solid #F3F4F6' }}>
      <span>{branchLabel(e.code)} <span style={{ color: '#9CA3AF' }}>({e.code})</span></span>
      <strong>{e.count}</strong>
    </div>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 18, width: 380, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{detail.cat_class} — locations</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>{detail.description}</div>
          </div>
          <button type="button" onClick={onClose} style={{ ...styles.btnSecondary, fontSize: 12, padding: '2px 8px' }}>✕</button>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#065F46', margin: '8px 0 2px' }}>
          Allocatable — parent branches ({sum(parents)})
        </div>
        {parents.length ? parents.map(e => <Row key={e.code} e={e} />)
          : <div style={{ fontSize: 12, color: '#9CA3AF' }}>None at a parent branch.</div>}
        {committed.length > 0 && (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#B45309', margin: '12px 0 2px' }}>
              Committed — on a TA / sub-yard, not allocatable ({sum(committed)})
            </div>
            {committed.map(e => <Row key={e.code} e={e} />)}
          </>
        )}
      </div>
    </div>
  );
}

const LABEL_W = 280;
const BRANCH_NAMES = {
  '100': 'Corpus Christi', '102': 'Corpus 102', '200': 'Beaumont', '300': 'Tulsa', '400': 'Gonzales',
  '500': 'Pasadena', '506': 'Pasadena 506', '600': 'Salt Lake City', '700': 'Detroit', '704': 'Detroit 704',
  '800': 'TA Division', '900': 'Rescue',
};
// Allocation columns: 800 (TA yard) first since it's the default pull point,
// then the fixed branches 100-700, plus 102 (a Corpus yard George treats as its
// own main allocatable branch, 2026-06-19). Sub-branch/committed units aren't
// shown — they're not allocatable (the location pop-ups show where they sit).
// 506 + 704 added 2026-07-09 (George): allocatable yards under Pasadena /
// Detroit — columns render even with zero RM stock so pulls can be planned.
const ALLOC_BRANCHES = ['800', '100', '102', '200', '300', '400', '500', '506', '600', '700', '704'];

// Job tab strip — flip a panel (and the page Focus) between each job on the
// Gantt and the All-jobs roll-up, so you don't have to scroll up to the Focus
// dropdown. Shared by the Manpower Demand card + the Allocation Worksheet
// (George 2026-06-29). Shown whenever ≥1 job is on the Gantt — the "All jobs
// (N)" count makes it obvious how many jobs are loaded (e.g. add the 2nd job
// via the picker if it reads "(1)").
function JobTabs({ allJobs, focusId, onFocus }) {
  if ((allJobs || []).length === 0) return null;
  const tabs = [{ id: null, label: `All jobs (${allJobs.length})` },
    ...allJobs.map(p => ({ id: p.id, label: `${p.job_number || p.site || `Job ${p.id}`}${p.scenario ? ' (scenario)' : ''}` }))];
  return (
    <div style={{ display: 'flex', gap: 2, marginBottom: 10, flexWrap: 'wrap', borderBottom: '1px solid #E5E7EB' }}>
      {tabs.map(t => {
        const active = (t.id == null && focusId == null) || t.id === focusId;
        return (
          <button key={t.id == null ? 'all' : t.id} type="button" onClick={() => onFocus(t.id)}
            title={t.id == null ? 'Combined demand across every job on the Gantt' : 'Scope this panel (and the analysis above) to this job'}
            style={{ border: 'none', cursor: 'pointer', fontSize: 13,
              padding: '6px 12px', fontWeight: active ? 700 : 500,
              background: active ? '#E36F2C' : 'none',
              color: active ? '#fff' : '#374151',
              borderRadius: active ? '6px 6px 0 0' : 0,
              borderBottom: active ? '2px solid #E36F2C' : '2px solid transparent',
              marginBottom: -1 }}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ---- Allocation worksheet --------------------------------------------------
// Per CAT-CLASS in the focused scope: peak Needed, the surplus/deficit at the
// 800 yard, and what's Available at each parent branch (100-800). Type a pull
// qty per branch and Remaining updates live. ✉ per branch opens a prefilled
// mail draft (recipient left blank — George sets it and sends from his mailbox)
// listing what to pull from that branch for the job(s) shown. The pull plan
// SAVES (2026-06-19): entries persist per Focus scope (the focused job, or the
// all-jobs roll-up) via /api/gantt/allocations. Click a CAT-CLASS to see where
// those units sit across the company.
function AllocationWorksheet({ rows, projects, allJobs, focusId, onFocus, onShowLocations }) {
  const [alloc, setAlloc] = useState({}); // alloc[cat_class][branchCode] = units
  const [savedKey, setSavedKey] = useState('');   // canonical form of the last-saved plan
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);    // brief "✓ Saved" flash
  const [rolled, toggleRolled] = useRollup('gantt-worksheet');
  const [ioError, setIoError] = useState('');
  // Column filters — view-only, they never touch the allocation state itself.
  const [q, setQ] = useState('');             // CAT-CLASS / description search
  const [show, setShow] = useState('all');    // all | short | covered | over
  const [stockAt, setStockAt] = useState(''); // '' = any branch, else a branch code
  // Third-party rentals: { cat_class: { qty, vendor_name, quote: {name,size}|null } }
  // — the re-rent plan for the gap owned stock can't cover (George 2026-06-19).
  const [tp, setTp] = useState({});
  const [tpBusy, setTpBusy] = useState(''); // cat_class mid-upload

  const planRows = useMemo(() => rows.filter(r => r.peak > 0), [rows]);

  const availAt = (r, code) => Number(r.avail_by_branch?.[code] || 0);
  const allocAt = (cat, code) => Number(alloc[cat]?.[code] || 0);
  const allocatedFor = (r) => ALLOC_BRANCHES.reduce((s, c) => s + allocAt(r.cat_class, c), 0);
  const tpQty = (cat) => Number(tp[cat]?.qty || 0);
  const setTpField = (cat, field, val) => setTp(prev => ({ ...prev, [cat]: { ...(prev[cat] || {}), [field]: val } }));

  // Rows actually shown — planRows narrowed by the column filters above.
  // Suggest / Clear / ✉ still act on the full planRows (the plan is the source
  // of truth); filters only scope what you see and what Export CSV writes.
  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return planRows.filter(r => {
      if (needle && !`${r.cat_class} ${r.description || ''}`.toLowerCase().includes(needle)) return false;
      if (stockAt && availAt(r, stockAt) <= 0) return false;
      if (show !== 'all') {
        const remaining = r.peak - allocatedFor(r) - tpQty(r.cat_class);
        if (show === 'short' && remaining <= 0) return false;
        if (show === 'covered' && remaining !== 0) return false;
        if (show === 'over' && remaining >= 0) return false;
      }
      return true;
    });
  }, [planRows, q, show, stockAt, alloc, tp]);

  const resetFilters = () => { setQ(''); setShow('all'); setStockAt(''); };

  // Header sort — George 2026-06-19: "sort by what's still needed +/- column."
  // The summary columns sort; ±@800 and Remaining are the headline ones. Sorting
  // by Remaining re-orders live as you allocate (covered rows sink), which is the
  // point — knock down the reds top-down. Branch columns aren't sortable (they
  // carry the ✉ action). Click a header to toggle asc/desc.
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (c) => {
    if (sortCol === c) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(c); setSortDir('asc'); }
  };
  const sortAccessors = {
    cat_class: r => r.cat_class,
    description: r => r.description || '',
    peak: r => r.peak,
    def800: r => (!r.avail_by_branch || !Object.keys(r.avail_by_branch).length) ? null : availAt(r, '800') - r.peak,
    allocated: r => allocatedFor(r),
    tp_qty: r => tpQty(r.cat_class),
    remaining: r => r.peak - allocatedFor(r) - tpQty(r.cat_class),
  };
  const sortedRows = useMemo(() => {
    if (!sortCol || !sortAccessors[sortCol]) return filteredRows;
    const acc = sortAccessors[sortCol];
    return [...filteredRows].sort((a, b) => {
      const av = acc(a), bv = acc(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredRows, sortCol, sortDir, alloc, tp]);
  // Sort indicator + clickable-header style for the summary columns.
  const sortInd = (col) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : <span style={{ opacity: 0.3 }}> ⇅</span>;
  const sortableTh = (col, label, { align = 'left', title } = {}) => (
    <th onClick={() => toggleSort(col)} title={title ? `${title} — click to sort` : 'Click to sort'}
      style={{ ...styles.th, textAlign: align, cursor: 'pointer', userSelect: 'none' }}>
      {label}{sortInd(col)}
    </th>
  );

  // --- Persistence (saves per Focus scope) ---------------------------------
  // Drop zeros/empties so {175-124:{800:0}} compares equal to {} ...
  const normalizeAlloc = (a) => {
    const out = {};
    for (const cat of Object.keys(a || {})) {
      for (const code of Object.keys(a[cat] || {})) {
        const n = Number(a[cat][code]) || 0;
        if (n > 0) (out[cat] = out[cat] || {})[code] = n;
      }
    }
    return out;
  };
  // ... and a stable string for dirty-checking (key order independent).
  const stableAlloc = (a) => {
    const n = normalizeAlloc(a);
    return Object.keys(n).sort().map(cat =>
      `${cat}:${Object.keys(n[cat]).sort().map(code => `${code}=${n[cat][code]}`).join(',')}`).join('|');
  };
  // Third-party qty + vendor ride the same Save (the quote FILE is saved on
  // upload, separately, so it's left out of the dirty key).
  const normalizeTp = (t) => {
    const out = {};
    for (const cat of Object.keys(t || {})) {
      const e = t[cat] || {};
      const qty = Math.max(0, Math.floor(Number(e.qty) || 0));
      const v = (e.vendor_name || '').trim();
      if (qty > 0 || v) out[cat] = { qty, vendor_name: v };
    }
    return out;
  };
  const stableTp = (t) => {
    const n = normalizeTp(t);
    return Object.keys(n).sort().map(cat => `${cat}:${n[cat].qty}/${n[cat].vendor_name}`).join('|');
  };
  const planKey = (a, t) => `${stableAlloc(a)}#TP#${stableTp(t)}`;
  const dirty = planKey(alloc, tp) !== savedKey;

  // Load the saved plan for the current Focus scope. Switching Focus loads that
  // scope's plan (each job — and the all-jobs roll-up — has its own).
  useEffect(() => {
    let live = true;
    setIoError('');
    api.ganttAllocations(focusId).then(res => {
      if (!live) return;
      const a = res.alloc || {};
      const t = res.thirdParty || {};
      setAlloc(a);
      setTp(t);
      setSavedKey(planKey(a, t));
    }).catch(e => { if (live) setIoError(e.message); });
    return () => { live = false; };
  }, [focusId]);

  const save = async () => {
    setSaving(true); setIoError('');
    try {
      await api.ganttSaveAllocations(focusId, normalizeAlloc(alloc), normalizeTp(tp));
      setSavedKey(planKey(alloc, tp));
      setSaveOk(true); setTimeout(() => setSaveOk(false), 2000);
    } catch (e) { setIoError(e.message); }
    finally { setSaving(false); }
  };

  // Warn before leaving with unsaved pull-plan edits.
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const setCell = (cat, code, val, max) => {
    const n = Math.max(0, Math.min(Math.floor(Number(val) || 0), max));
    setAlloc(prev => ({ ...prev, [cat]: { ...(prev[cat] || {}), [code]: n } }));
  };

  // Vendor quote file — saved immediately (separate from the qty/vendor Save).
  const uploadQuote = (cat, file) => {
    if (!file) return;
    setTpBusy(cat); setIoError('');
    api.ganttUploadTpQuote(focusId, cat, file)
      .then(res => setTp(prev => ({ ...prev, [cat]: { ...(prev[cat] || {}), quote: res.quote } })))
      .catch(e => setIoError(e.message))
      .finally(() => setTpBusy(''));
  };
  const downloadQuote = (cat, name) => api.ganttDownloadTpQuote(focusId, cat, name).catch(e => setIoError(e.message));
  const deleteQuote = (cat) => {
    if (!confirm('Remove the vendor quote for this item?')) return;
    api.ganttDeleteTpQuote(focusId, cat)
      .then(() => setTp(prev => ({ ...prev, [cat]: { ...(prev[cat] || {}), quote: null } })))
      .catch(e => setIoError(e.message));
  };

  // Greedy starting plan: cover each row's need from 800 first, then 100→700,
  // capped by what's Available at each branch. Overwrites the current entries.
  // Fill the 800 yard ONLY (George 2026-06-19: Suggest must not auto-fill the
  // other branches). Sets each row's 800 cell to what's still needed after any
  // manual entries at other branches, capped by 800 availability — other
  // branch columns are left untouched.
  const suggest = () => {
    setAlloc(prev => {
      const next = { ...prev };
      for (const r of planRows) {
        const cur = next[r.cat_class] || {};
        const fromOthers = ALLOC_BRANCHES.reduce((s, c) => c === '800' ? s : s + (Number(cur[c]) || 0), 0);
        const need = Math.max(0, r.peak - fromOthers);
        next[r.cat_class] = { ...cur, '800': Math.min(need, availAt(r, '800')) };
      }
      return next;
    });
  };

  const scope = useMemo(
    () => projects.map(p => ({ label: p.job_number || p.site || `#${p.id}`, site: p.site, fp: projFootprint(p) })),
    [projects]
  );

  const emailBranch = (code) => {
    const lines = planRows
      .map(r => ({ r, qty: allocAt(r.cat_class, code) }))
      .filter(x => x.qty > 0);
    if (!lines.length) {
      alert(`Nothing allocated from ${branchLabel(code)} (${code}) yet — enter pull quantities in the ${code} column first.`);
      return;
    }
    const isYard = code === '800';
    const jobsTxt = scope
      .map(j => `  ${j.label}${j.site && j.site !== j.label ? ` (${j.site})` : ''} — ${fmtDate(j.fp.start)} to ${fmtDate(j.fp.end)}`)
      .join('\n');
    const body =
      `Branch ${code} — ${branchLabel(code)}\n\n` +
      `Please ${isYard ? 'pull and stage' : 'transfer'} the following equipment for:\n${jobsTxt}\n\n` +
      lines.map(x => `  ${x.qty} x  ${x.r.cat_class}  ${x.r.description}   (available at ${code}: ${availAt(x.r, code)})`).join('\n') +
      `\n\nPlease confirm availability and coordinate delivery or pickup. Thank you.`;
    const subject = `SSS equipment ${isYard ? 'pull' : 'transfer'} request — ${branchLabel(code)} (${code})`;
    const a = document.createElement('a');
    a.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  // Export the CURRENTLY FILTERED worksheet (what you see) to CSV: per-branch
  // pull qty + availability, plus Needed / Net @800 / Alloc / Remaining.
  const exportCsv = () => {
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ['CAT-CLASS', 'Description', 'Needed', 'Net @800'];
    for (const code of ALLOC_BRANCHES) header.push(`${code} Pull`, `${code} Avail`);
    header.push('Allocated', '3rd Party Qty', 'Vendor', 'Quote', 'Remaining');
    const out = [header];
    for (const r of sortedRows) {
      const allocated = allocatedFor(r);
      const noStock = !r.avail_by_branch || Object.keys(r.avail_by_branch).length === 0;
      const t = tp[r.cat_class] || {};
      const tpQ = Number(t.qty) || 0;
      const line = [r.cat_class, r.description || '', r.peak, noStock ? '' : availAt(r, '800') - r.peak];
      for (const code of ALLOC_BRANCHES) line.push(allocAt(r.cat_class, code) || 0, availAt(r, code));
      line.push(allocated, tpQ || '', t.vendor_name || '', t.quote ? t.quote.name : '', r.peak - allocated - tpQ);
      out.push(line);
    }
    const bom = String.fromCharCode(0xFEFF); // so Excel reads the file as UTF-8
    const csv = bom + out.map(cols => cols.map(esc).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `allocation-worksheet-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.card}>
      <RollupH2 collapsed={rolled} onToggle={toggleRolled} style={{ marginBottom: rolled ? 0 : undefined }}>Allocation Worksheet <span style={{ fontWeight: 400, fontSize: 12, color: '#6B7280' }}>· plan the pull by branch, then email</span></RollupH2>
      {!rolled && <>
      {/* Job tabs — flip the worksheet (and the page Focus) between each job and
          the All-jobs roll-up; each tab loads its own saved pull plan. */}
      <JobTabs allJobs={allJobs} focusId={focusId} onFocus={onFocus} />
      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
        Per CAT-CLASS in the {focusId == null ? 'jobs shown' : 'focused job'}: peak <strong>Needed</strong>, the surplus/deficit at the <strong>800</strong> yard, and what's Available at each parent branch (100–800).
        Type how many units to pull from each branch — <strong>Remaining</strong> updates live, then <strong>💾 Save</strong> to keep the plan (saved for {focusId == null ? 'the all-jobs roll-up' : 'this job'}; switching Focus loads that view's plan). <strong>✉</strong> a branch column to open a prefilled email (you set the recipient and send); click a <strong>CAT-CLASS</strong> to see where its units sit. Available counts are companywide RentalMan as of the freshness chip above; sub-branch / committed units aren't allocatable.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={save} disabled={saving || !dirty}
          title="Save the pull plan for this view (the focused job, or the all-jobs roll-up)."
          style={{ ...styles.btn, fontSize: 12, padding: '6px 12px', background: dirty ? '#E36F2C' : undefined, opacity: (saving || !dirty) ? 0.6 : 1 }}>
          {saving ? 'Saving…' : '💾 Save'}</button>
        <span style={{ fontSize: 12, fontWeight: 600, color: dirty ? '#B45309' : saveOk ? '#10B981' : '#9CA3AF' }}>
          {dirty ? '● Unsaved changes' : saveOk ? '✓ Saved' : 'Saved'}</span>
        <span style={{ width: 1, alignSelf: 'stretch', background: '#E5E7EB', margin: '0 2px' }} />
        <button type="button" style={{ ...styles.btnSecondary, fontSize: 12 }} onClick={suggest}
          title="Fill each row's 800-yard pull only (what's still needed after any manual entries, capped by 800 availability). Other branches and 3rd-party are left for you to set.">✨ Fill from 800</button>
        <button type="button" style={{ ...styles.btnSecondary, fontSize: 12 }} onClick={() => setAlloc({})}>Clear</button>
        <span style={{ width: 1, alignSelf: 'stretch', background: '#E5E7EB', margin: '0 2px' }} />
        <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Filter CAT-CLASS or description"
          style={{ ...styles.input, fontSize: 12, padding: '4px 8px', width: 220 }} />
        <select value={show} onChange={e => setShow(e.target.value)} style={{ ...styles.input, fontSize: 12, padding: '4px 8px' }}
          title="Filter by coverage: Short = still need units, Covered = exactly met, Over = allocated more than needed.">
          <option value="all">Coverage: all</option>
          <option value="short">Short only</option>
          <option value="covered">Covered only</option>
          <option value="over">Over-allocated</option>
        </select>
        <select value={stockAt} onChange={e => setStockAt(e.target.value)} style={{ ...styles.input, fontSize: 12, padding: '4px 8px' }}
          title="Show only rows with units Available at the chosen branch.">
          <option value="">Stock at: any branch</option>
          {ALLOC_BRANCHES.map(code => (
            <option key={code} value={code}>{`Stock at ${code} · ${branchLabel(code).split(' ')[0]}`}</option>
          ))}
        </select>
        {(q || show !== 'all' || stockAt) && (
          <button type="button" style={{ ...styles.btnSecondary, fontSize: 12 }} onClick={resetFilters}>Reset</button>
        )}
        <span style={{ fontSize: 12, color: '#6B7280' }}>{filteredRows.length} of {planRows.length}</span>
        <button type="button" style={{ ...styles.btnSecondary, fontSize: 12, marginLeft: 'auto' }} onClick={exportCsv}
          disabled={filteredRows.length === 0} title="Download the rows shown (after filters) as a CSV.">⬇ Export CSV</button>
      </div>
      {ioError && <div style={{ fontSize: 12, color: '#B91C1C', marginBottom: 8 }}>{ioError}</div>}
      {planRows.length === 0 ? (
        <div style={{ fontSize: 13, color: '#6B7280' }}>No matched equipment demand to allocate.</div>
      ) : filteredRows.length === 0 ? (
        <div style={{ fontSize: 13, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 8 }}>
          No rows match the filters.
          <button type="button" style={{ ...styles.btnSecondary, fontSize: 12 }} onClick={resetFilters}>Reset filters</button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {sortableTh('cat_class', 'CAT-CLASS')}
                {sortableTh('description', 'Description')}
                {sortableTh('peak', 'Needed', { align: 'right', title: 'Peak concurrent quantity needed across the jobs shown' })}
                {sortableTh('def800', '± @800', { align: 'right', title: 'Available at the 800 yard minus Needed. Positive = surplus, negative = deficit' })}
                {ALLOC_BRANCHES.map(code => (
                  <th key={code} style={{ ...styles.th, textAlign: 'center', whiteSpace: 'nowrap', padding: '8px 3px', background: code === '800' ? '#FFF7ED' : undefined }}
                    title={`${branchLabel(code)} (${code}) — Available units here. Type a pull qty per row, then ✉ to email this branch.`}>
                    {code}
                    <div style={{ fontWeight: 400, fontSize: 9, color: '#9CA3AF' }}>{branchLabel(code).split(' ')[0]}</div>
                    <button type="button" onClick={() => emailBranch(code)} title={`Email a pull list for branch ${code}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1D4ED8', fontSize: 13, padding: 0, marginTop: 2 }}>✉</button>
                  </th>
                ))}
                {sortableTh('allocated', 'Alloc', { align: 'right', title: 'Total owned units allocated across all branches for this row' })}
                {sortableTh('tp_qty', '3rd Party Rental', { align: 'center', title: 'Units to rent from a 3rd-party vendor for the gap owned stock cannot cover — set the qty, name the vendor, and upload their quote' })}
                {sortableTh('remaining', 'Remaining', { align: 'right', title: 'Needed − Owned allocated − 3rd-party rental. Red = still short; green ✓ = fully sourced' })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(r => {
                const allocated = allocatedFor(r);
                const tpRec = tp[r.cat_class] || {};
                const tpQ = Number(tpRec.qty) || 0;
                const gap = r.peak - allocated;          // still short before 3rd party
                const remaining = gap - tpQ;             // final unsourced gap
                const noStock = !r.avail_by_branch || Object.keys(r.avail_by_branch).length === 0;
                const def800 = availAt(r, '800') - r.peak;
                return (
                  <tr key={r.cat_class}>
                    <td style={styles.td}>
                      {r.avail_by_branch && Object.keys(r.avail_by_branch).length > 0 ? (
                        <button type="button"
                          onClick={() => onShowLocations({ cat_class: r.cat_class, description: r.description, avail_by_branch: r.avail_by_branch })}
                          title="Show where these units sit across the company"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1D4ED8', fontWeight: 600, textDecoration: 'underline', fontSize: 'inherit' }}>
                          {r.cat_class}
                        </button>
                      ) : r.cat_class}
                      {r.scenarioOnly && <span style={scenarioBadge}>SCEN</span>}
                    </td>
                    <td style={styles.td}>{r.description}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{r.peak}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, color: noStock ? '#9CA3AF' : def800 < 0 ? '#B91C1C' : '#10B981' }}>
                      {noStock ? '—' : def800 > 0 ? `+${def800}` : def800}
                    </td>
                    {ALLOC_BRANCHES.map(code => {
                      const av = availAt(r, code);
                      const val = allocAt(r.cat_class, code);
                      return (
                        <td key={code} style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap', padding: '8px 3px', background: code === '800' ? '#FFF7ED' : undefined }}>
                          {/* Editable even at zero shown stock (George 2026-07-09) — RM can
                              lag reality, and stock can be moved TO a branch for the pull.
                              No max cap; the /N hint still shows what RM says is there. */}
                          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
                            <input type="number" min={0} value={val || ''}
                              onChange={e => setCell(r.cat_class, code, e.target.value, Infinity)}
                              style={{ ...styles.input, width: 44, padding: '2px 3px', fontSize: 12, textAlign: 'right' }} />
                            <span style={{ fontSize: 9, color: av > 0 ? '#9CA3AF' : '#D1D5DB' }}>/{av}</span>
                          </span>
                        </td>
                      );
                    })}
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{allocated || ''}</td>
                    <td style={{ ...styles.td, whiteSpace: 'nowrap', verticalAlign: 'top', background: '#FAFAFA' }}>
                      <input type="number" min={0} value={tpQ || ''} placeholder={gap > 0 ? String(gap) : ''}
                        onChange={e => setTpField(r.cat_class, 'qty', Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                        title="Units to rent from a 3rd party (the gap owned stock can't cover)"
                        style={{ ...styles.input, width: 56, padding: '2px 4px', fontSize: 12, textAlign: 'right' }} />
                      <input type="text" value={tpRec.vendor_name || ''} placeholder="Vendor"
                        onChange={e => setTpField(r.cat_class, 'vendor_name', e.target.value)}
                        style={{ ...styles.input, width: 96, padding: '2px 4px', fontSize: 11, marginTop: 3, display: 'block' }} />
                      <div style={{ marginTop: 3, fontSize: 11 }}>
                        {tpRec.quote ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 130 }}>
                            <button type="button" onClick={() => downloadQuote(r.cat_class, tpRec.quote.name)} title={tpRec.quote.name}
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1D4ED8', textDecoration: 'underline', fontSize: 11, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {tpRec.quote.name}</button>
                            <button type="button" onClick={() => deleteQuote(r.cat_class)} title="Remove quote"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B91C1C', fontSize: 12, padding: 0, lineHeight: 1 }}>✕</button>
                          </span>
                        ) : (
                          <DropZone onFiles={files => uploadQuote(r.cat_class, files[0])} disabled={tpBusy === r.cat_class} title="Drop the vendor's quote here">
                            <label style={{ cursor: 'pointer', color: '#1D4ED8' }} title="Upload the vendor's quote (PDF or image)">
                              {tpBusy === r.cat_class ? 'Uploading…' : '📎 Upload quote'}
                              <input type="file" style={{ display: 'none' }}
                                onChange={e => { uploadQuote(r.cat_class, e.target.files[0]); e.target.value = ''; }} />
                            </label>
                          </DropZone>
                        )}
                      </div>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, verticalAlign: 'top', color: remaining > 0 ? '#B91C1C' : remaining < 0 ? '#B45309' : '#10B981' }}>
                      {remaining > 0 ? remaining : remaining < 0 ? `over ${-remaining}` : '✓'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </>}
    </div>
  );
}
// Parent (allocatable) branches: round-100 division codes + 102 (George 2026-06-19).
const isParentCode = (c) => { const n = parseInt(c, 10); return Number.isInteger(n) && ((n >= 100 && n % 100 === 0) || [102, 506, 704].includes(n)); };
const isTACode = (c) => { const n = parseInt(c, 10); return Number.isInteger(n) && n >= 801 && n <= 899; };
const branchLabel = (code) => BRANCH_NAMES[code] || `${code}${isTACode(code) ? ' (TA)' : ' (sub-yard)'}`;
const pct = (iso, window_, totalDays) =>
  `${Math.min(Math.max((daysBetween(window_.start, iso) / totalDays) * 100, 0), 100)}%`;

const scenarioBadge = {
  marginLeft: 6, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
  background: '#F3F4F6', color: '#6B7280', border: '1px dashed #9CA3AF', verticalAlign: 'middle'
};

const fmtDateTimeShort = (iso) => {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Inventory freshness chip — mirrors SSS-Jorge's (green when fresh, red +
// pulsing dot when the report is overdue). Jorge's scrape is daily (25h
// threshold); the CRM equipment report is pushed every 2 hours, so anything
// older than STALE_HOURS means the scraper likely missed a run.
const STALE_HOURS = 3;
const relAgeLabel = (ms) => {
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  return `${Math.floor(min / 60)}h ${min % 60}m ago`;
};
function FreshnessChip({ lastModified, stale }) {
  const base = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, letterSpacing: '.03em', padding: '4px 10px', borderRadius: 99, width: 'fit-content' };
  const dot = { width: 7, height: 7, borderRadius: '50%', display: 'inline-block', flexShrink: 0 };
  if (!lastModified) {
    return <span style={{ ...base, background: 'rgba(148,163,184,.12)', color: '#64748B', border: '1px solid rgba(148,163,184,.3)' }}><span style={{ ...dot, background: '#94A3B8' }} />Stock: unavailable</span>;
  }
  const ageMs = Date.now() - new Date(lastModified).getTime();
  const overdue = ageMs >= STALE_HOURS * 3600000;
  const rel = relAgeLabel(ageMs);
  const asOf = `Report last updated ${fmtDateTimeShort(lastModified)} (Rental Man, companywide). Scraper pushes every 2 hours.`;
  if (overdue) {
    return (
      <span title={asOf} style={{ ...base, background: 'rgba(239,68,68,.12)', color: '#DC2626', border: '1px solid rgba(239,68,68,.35)' }}>
        <style>{'@keyframes ggPulseDot{0%,100%{opacity:1}50%{opacity:.25}}'}</style>
        <span style={{ ...dot, background: '#EF4444', animation: 'ggPulseDot 1.2s ease-in-out infinite' }} />
        ⚠ Stock: {rel} — scraper may have missed a run (every 2h){stale ? ' · R2 cached' : ''}
      </span>
    );
  }
  return (
    <span title={asOf} style={{ ...base, background: 'rgba(34,197,94,.12)', color: '#16A34A', border: '1px solid rgba(34,197,94,.3)' }}>
      <span style={{ ...dot, background: '#22C55E' }} />
      Stock: {rel}{stale ? ' · R2 cached' : ''}
    </span>
  );
}

// ---- Add-to-Gantt picker -----------------------------------------------

function AddProjectPicker({ busy, onAdded, setError }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      api.ganttCandidates(q).then(r => { if (live) setResults(r); }).catch(() => {});
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, open]);

  const add = async (id) => {
    setError('');
    try { await api.ganttToggle(id, true); setOpen(false); setQ(''); onAdded(); }
    catch (e) { setError(e.message); }
  };

  return (
    <div style={{ ...styles.card, paddingBottom: open ? 16 : 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={styles.btn} disabled={busy} onClick={() => setOpen(!open)}>
          {open ? '✕ Close' : '➕ Add Job to Gantt'}
        </button>
        {open && (
          <input
            autoFocus
            style={{ ...styles.input, width: 340 }}
            placeholder="Search by site or job number…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        )}
        {!open && <span style={{ fontSize: 12, color: '#6B7280' }}>
          Flip any project — including an open bid — onto the Gantt to see its equipment impact. Flip it back off any time.
        </span>}
      </div>
      {open && (
        <table style={{ ...styles.table, marginTop: 10 }}>
          <thead>
            <tr>
              <th style={styles.th}>Site</th>
              <th style={styles.th}>Job #</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Start</th>
              <th style={styles.th}>Branch</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {results.length === 0 && (
              <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>No matching projects.</td></tr>
            )}
            {results.map(r => (
              <tr key={r.id}>
                <td style={styles.td}>{r.site}</td>
                <td style={styles.td}>{r.job_number || '—'}</td>
                <td style={styles.td}><StatusPill status={r.status} /></td>
                <td style={styles.td}>{fmtDate(r.date_planned)}</td>
                <td style={styles.td}>{r.branch_code || ''}</td>
                <td style={styles.td}>
                  <button style={{ ...styles.btnSecondary, fontSize: 12 }} onClick={() => add(r.id)}>
                    {['Won', 'Active', 'Complete'].includes(r.status) ? '➕ Add' : '➕ Add as scenario'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---- Per-project detail: lines + punch list ------------------------------

const emptyEquip = { description: '', cat_class: '', catalog_id: '', qty: '', start_date: '', end_date: '' };
const emptyLabor = { craft: '', day_hc: '', night_hc: '', start_date: '', end_date: '' };

const PROJECT_STATUSES = ['In the Hunt', 'Bidding', 'Mobilization Standby', 'Won', 'Active', 'Complete', 'Lost', 'Closed'];

// Ops-side job editor (2026-06-12): job number, status, site, branch, start,
// duration — written to the SAME projects row sales sees (one record; the
// change shows in the opportunity's History). Flipping a scenario to Won here
// is what unlocks the punch-list push.
function JobEditForm({ project, onSaved, onCancel, setError }) {
  const initial = {
    job_number: project.job_number || '',
    status: project.status,
    site: project.site || '',
    branch_id: '',
    date_planned: project.date_planned || '',
    duration_days: project.duration_days || ''
  };
  const [f, setF] = useState(initial);
  // What the form opened with. Save sends only the fields the user changed:
  // the Gantt feed loads once, so a whole-row save would write back any value
  // another screen changed since (a status, a start date, a job number) and
  // push that stale value to Field Services. Same rule as the Project Tracker.
  const opened = useRef(initial);
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.listBranches().then(bs => {
      setBranches(bs);
      const cur = bs.find(b => b.code === project.branch_code);
      if (cur) {
        opened.current = { ...opened.current, branch_id: cur.id };
        setF(prev => ({ ...prev, branch_id: cur.id }));
      }
    }).catch(() => {});
    api.customers().then(cs => setCustomers(cs || [])).catch(() => {}); // suggestions only; free text is fine
  }, []);

  const save = async () => {
    // Compared the way the server cleans them (routes/gantt.js PUT), so a
    // value that only looks different (padding, '' vs null) is not a change.
    const numOrNull = (v) => (v === '' || v == null ? null : Number(v));
    const clean = {
      job_number: (v) => String(v || '').trim(),
      status: (v) => v || '',
      site: (v) => String(v || '').trim(),
      branch_id: numOrNull,
      date_planned: (v) => String(v || ''),
      duration_days: numOrNull,
    };
    const changes = {};
    for (const k of Object.keys(clean)) {
      if (Object.is(clean[k](f[k]), clean[k](opened.current[k]))) continue;
      changes[k] = k === 'branch_id' ? numOrNull(f.branch_id) : f[k];
    }
    if (!Object.keys(changes).length) { onCancel(); return; }
    setSaving(true);
    setError('');
    try {
      await api.ganttUpdateProject(project.id, changes);
      onSaved();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  const inSm = { ...styles.input, padding: '4px 6px', fontSize: 12 };
  const lbl = { fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 2 };
  return (
    <div style={{ background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 6, padding: 10, marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={lbl}>Job #</label>
          <input style={{ ...inSm, width: 170 }} placeholder="820-26-CUST-CITYST"
            value={f.job_number} onChange={e => setF({ ...f, job_number: e.target.value })} />
        </div>
        <div>
          <label style={lbl}>Status</label>
          <select style={{ ...inSm, width: 120 }} value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
            {PROJECT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Site / Customer</label>
          <input style={{ ...inSm, width: 220 }} list={`gantt-cust-${project.id}`}
            value={f.site} onChange={e => setF({ ...f, site: e.target.value })} />
          <datalist id={`gantt-cust-${project.id}`}>
            {customers.slice(0, 500).map(c => <option key={c.id} value={c.name} />)}
          </datalist>
        </div>
        <div>
          <label style={lbl}>Branch</label>
          <select style={{ ...inSm, width: 130 }} value={f.branch_id} onChange={e => setF({ ...f, branch_id: e.target.value })}>
            <option value="">—</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.code}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Start</label>
          <input type="date" style={{ ...inSm, width: 130 }} value={f.date_planned}
            onChange={e => setF({ ...f, date_planned: e.target.value })} />
        </div>
        <div>
          <label style={lbl}>Duration (days)</label>
          <input style={{ ...inSm, width: 70 }} value={f.duration_days}
            onChange={e => setF({ ...f, duration_days: e.target.value })} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={{ ...styles.btn, fontSize: 12 }} disabled={saving || !f.site.trim()} onClick={save}>💾 Save</button>
          <button style={{ ...styles.btnSecondary, fontSize: 12 }} disabled={saving} onClick={onCancel}>Cancel</button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#9A3412', marginTop: 6 }}>
        Edits write to the sales opportunity itself — status, dates, and site change everywhere, and the change is logged on the project's History.
      </div>
    </div>
  );
}

// Single-project daily labor Gantt — one row per craft with phase-segment
// bars (headcount labeled, day=orange / night=indigo) over the project's labor
// span, plus a stepped "total on-site crew per day" track underneath. Ops view:
// counts only (no rates). Reads the feed's per-phase labor_lines directly.
function LaborSchedule({ project }) {
  const segs = (project.labor_lines || [])
    .map(l => {
      const day = Number(l.day_hc) || 0, night = Number(l.night_hc) || 0;
      if (day + night === 0) return null;
      const pw = projWindow(project);
      return { craft: l.craft || '—', start: l.start_date || pw.start, end: l.end_date || pw.end, day, night };
    })
    .filter(Boolean);
  if (!segs.length) return (
    <div style={{ fontSize: 12, color: '#9CA3AF', margin: '8px 0' }}>
      Daily Labor Schedule — no staffed labor phases on this project's estimate yet.
    </div>
  );

  let lo = null, hi = null;
  for (const s of segs) { if (!lo || s.start < lo) lo = s.start; if (!hi || s.end > hi) hi = s.end; }
  const win = { start: addDays(lo, -2), end: addDays(hi, 2) };
  const totalDays = Math.max(daysBetween(win.start, win.end), 1);

  const byCraft = new Map();
  for (const s of segs) { if (!byCraft.has(s.craft)) byCraft.set(s.craft, []); byCraft.get(s.craft).push(s); }

  // Stepped daily total: between consecutive phase boundaries the on-site
  // crew is constant, so render one segment per interval labeled with the count.
  const bounds = [...new Set(segs.flatMap(s => [s.start, addDays(s.end, 1)]))].sort();
  const intervals = [];
  for (let i = 0; i < bounds.length - 1; i++) {
    const a = bounds[i];
    const total = segs.filter(s => s.start <= a && addDays(s.end, 1) > a).reduce((t, s) => t + s.day + s.night, 0);
    if (total > 0) intervals.push({ start: a, end: bounds[i + 1], total });
  }
  const peak = intervals.reduce((m, iv) => Math.max(m, iv.total), 0) || 1;

  const LW = 190;
  const ticks = (() => {
    const out = []; const d = toUTC(win.start); d.setUTCDate(1);
    if (isoOf(d) < win.start) d.setUTCMonth(d.getUTCMonth() + 1);
    while (isoOf(d) <= win.end) { out.push({ iso: isoOf(d), label: `${d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })} '${String(d.getUTCFullYear()).slice(-2)}` }); d.setUTCMonth(d.getUTCMonth() + 1); }
    return out;
  })();

  return (
    <div style={{ margin: '8px 0 4px' }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
        Daily Labor Schedule{' '}
        <span style={{ fontWeight: 400, color: '#6B7280' }}>· {fmtDate(lo)} → {fmtDate(hi)} · peak {peak} on-site</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 640 }}>
          <div style={{ position: 'relative', height: 16, marginLeft: LW, borderBottom: '1px solid #E5E7EB' }}>
            {ticks.map(t => (
              <div key={t.iso} style={{ position: 'absolute', left: pct(t.iso, win, totalDays), top: 0, fontSize: 10, color: '#6B7280', borderLeft: '1px solid #E5E7EB', paddingLeft: 3, height: '100%' }}>{t.label}</div>
            ))}
          </div>
          {[...byCraft.entries()].map(([craft, rows]) => (
            <div key={craft} style={{ display: 'flex', alignItems: 'center', minHeight: 22, borderBottom: '1px solid #F8FAFC' }}>
              <div style={{ width: LW, flexShrink: 0, fontSize: 11, paddingRight: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={craft}>{craft}</div>
              <div style={{ position: 'relative', flexGrow: 1, height: 16 }}>
                {rows.map((s, i) => {
                  const left = pct(s.start, win, totalDays);
                  const width = `${Math.max(daysBetween(s.start, addDays(s.end, 1)) / totalDays * 100, 0.6)}%`;
                  const night = s.night > 0 && s.day === 0;
                  const mixed = s.night > 0 && s.day > 0;
                  const label = mixed ? `${s.day}D ${s.night}N` : night ? `${s.night}N` : `${s.day}`;
                  const bg = mixed ? `linear-gradient(90deg, ${colors.orange} 50%, #4338CA 50%)` : night ? '#4338CA' : colors.orange;
                  return <div key={i} title={`${craft}: ${fmtDate(s.start)} → ${fmtDate(s.end)} · ${label}`}
                    style={{ position: 'absolute', left, width, height: 16, borderRadius: 3, background: bg, color: '#fff', fontSize: 9, fontWeight: 700, textAlign: 'center', lineHeight: '16px', overflow: 'hidden', whiteSpace: 'nowrap' }}>{label}</div>;
                })}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', minHeight: 24, borderTop: '2px solid #1F2937', marginTop: 2 }}>
            <div style={{ width: LW, flexShrink: 0, fontSize: 11, fontWeight: 700, paddingRight: 6 }}>Total on-site / day</div>
            <div style={{ position: 'relative', flexGrow: 1, height: 18 }}>
              {intervals.map((iv, i) => {
                const left = pct(iv.start, win, totalDays);
                const width = `${Math.max(daysBetween(iv.start, iv.end) / totalDays * 100, 0.6)}%`;
                return <div key={i} title={`${fmtDate(iv.start)} → ${fmtDate(addDays(iv.end, -1))}: ${iv.total} on-site`}
                  style={{ position: 'absolute', left, width, height: 18, background: `rgba(230,111,44,${(0.25 + 0.6 * (iv.total / peak)).toFixed(2)})`, borderRight: '1px solid #fff', textAlign: 'center', fontSize: 9, fontWeight: 700, color: '#1F2937', lineHeight: '18px', overflow: 'hidden' }}>{iv.total}</div>;
              })}
            </div>
          </div>
        </div>
      </div>
      <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 3 }}>
        Day shifts orange, night indigo; numbers are headcount on shift. Bars span each phase. (Bill-side per-diem/travel + CSV export coming next.)
      </div>
    </div>
  );
}

function ProjectDetail({ project, busy, setError, onToggleOff, onChanged }) {
  const [newEquip, setNewEquip] = useState(emptyEquip);
  const [newLabor, setNewLabor] = useState(emptyLabor);
  const [saving, setSaving] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [editingJob, setEditingJob] = useState(false);

  // Catalog suggestions for the equipment add row — same picker behavior as
  // the estimate's Equipment section (George 2026-06-12): type to search the
  // catalog, click to fill description + CAT-CLASS; free text still allowed
  // for items not in the catalog.
  const [catalogQ, setCatalogQ] = useState('');
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    if (!catalogQ.trim()) { setCatalog([]); return; }
    let live = true;
    const t = setTimeout(() => {
      api.ganttCatalog(catalogQ).then(r => { if (live) setCatalog(r); }).catch(() => {});
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [catalogQ]);

  const pickCatalogItem = (item) => {
    setNewEquip({
      ...newEquip,
      description: item.description,
      cat_class: item.cat_class || '',
      catalog_id: item.id
    });
    setCatalogQ('');
    setCatalog([]);
  };

  const run = async (fn) => {
    setSaving(true);
    setError('');
    try { await fn(); onChanged(); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // Push to Inventory: confirmed jobs with a job number only. Scenario jobs
  // never get the button (and the server blocks them regardless).
  const canPush = !project.scenario && !!project.job_number;
  const doPush = async () => {
    const again = project.last_push?.status === 'ok';
    if (!window.confirm(
      again
        ? `Re-push the punch list for ${project.job_number}? This SUPERSEDES the list at the warehouse — Inventory will show added/removed/changed lines against what's already pulled.`
        : `Push the punch list for ${project.job_number} to Inventory? The warehouse will see it as a pull request on this job number.`
    )) return;
    setSaving(true);
    setError('');
    setPushResult(null);
    try {
      const r = await api.ganttPush(project.id);
      const inv = r.inventory || {};
      const d = inv.delta || {};
      setPushResult(
        `Push #${r.push_seq} delivered — ${inv.action === 'superseded' ? 'superseded the existing list' : 'created a new pull list'} ` +
        `(${inv.matched_lines} line(s)${inv.unmatched_lines ? `, ${inv.unmatched_lines} for warehouse review` : ''}` +
        (inv.action === 'superseded' ? `; delta +${(d.added || []).length}/−${(d.removed || []).length}/~${(d.changed || []).length}` : '') + ').'
      );
      onChanged();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const addEquip = () => run(async () => {
    await api.ganttAddEquipLine(project.id, {
      description: newEquip.description, cat_class: newEquip.cat_class,
      catalog_id: newEquip.catalog_id || null,
      qty: newEquip.qty === '' ? 0 : newEquip.qty,
      start_date: newEquip.start_date, end_date: newEquip.end_date
    });
    setNewEquip(emptyEquip);
    setCatalogQ('');
  });
  const addLabor = () => run(async () => {
    await api.ganttAddLaborLine(project.id, {
      craft: newLabor.craft,
      day_hc: newLabor.day_hc === '' ? 0 : newLabor.day_hc,
      night_hc: newLabor.night_hc === '' ? 0 : newLabor.night_hc,
      start_date: newLabor.start_date, end_date: newLabor.end_date
    });
    setNewLabor(emptyLabor);
  });

  const tdSm = { ...styles.td, fontSize: 12, padding: '5px 8px' };
  const inSm = { ...styles.input, padding: '4px 6px', fontSize: 12 };

  return (
    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, margin: '4px 0 10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 13 }}>
          <strong>{project.job_number || '(no job #)'}</strong> · {project.site} · <StatusPill status={project.status} />
          {project.estimates && project.estimates.length > 0 && <span style={{ marginLeft: 8, color: '#6B7280' }}>Lines from {project.estimates.map(e => `${e.label} (${e.status})`).join(', ')}</span>}
          {/* Ops date is the truth (server utils/opsDateShift.js): estimate dates are shown slid to it. */}
          {project.ops_shift_days ? <span style={{ marginLeft: 8, color: '#B45309' }}>Estimate dates moved {project.ops_shift_days > 0 ? '+' : ''}{project.ops_shift_days} day{Math.abs(project.ops_shift_days) === 1 ? '' : 's'} to match the Ops start date</span> : null}
          {project.origin === 'ops_manual' && <span style={{ marginLeft: 8, color: '#6B7280' }}>Ops-manual job (no estimate)</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {project.last_push && (
            <span style={{ fontSize: 12, color: project.last_push.status === 'ok' ? '#10B981' : '#B91C1C' }}>
              {project.last_push.status === 'ok'
                ? `Pushed rev ${project.last_push.push_seq} · ${fmtDate(project.last_push.created_at)}`
                : `Last push FAILED · ${fmtDate(project.last_push.created_at)}`}
            </span>
          )}
          {canPush && (
            <button style={styles.btn} disabled={busy || saving} onClick={doPush}>
              📤 {project.last_push?.status === 'ok' ? 'Re-push to Inventory' : 'Push to Inventory'}
            </button>
          )}
          <button style={styles.btnSecondary} disabled={busy || saving} onClick={() => setEditingJob(!editingJob)}>
            ✎ Edit job
          </button>
          <button style={styles.btnSecondary} disabled={busy || saving}
            onClick={() => downloadBlob(api.ganttPunchlist(project.id), `punchlist-${project.job_number || project.id}.xlsx`).catch(e => setError(e.message))}>
            ⬇ Punch List
          </button>
          <button style={styles.btnSecondary} disabled={busy || saving}
            title="Manual fallback: download this CSV and upload it on Inventory → Punch Lists → Import"
            onClick={() => downloadBlob(api.ganttPunchlistCsv(project.id), `punchlist-${project.job_number || project.id}.csv`).catch(e => setError(e.message))}>
            ⬇ CSV (Inventory)
          </button>
          <button style={{ ...styles.btnSecondary, color: '#B91C1C' }} disabled={busy || saving} onClick={onToggleOff}>
            ✕ Remove from Gantt
          </button>
        </div>
      </div>

      {editingJob && (
        <JobEditForm project={project} setError={setError}
          onSaved={() => { setEditingJob(false); onChanged(); }}
          onCancel={() => setEditingJob(false)} />
      )}

      {pushResult && (
        <div style={{ background: '#ECFDF5', border: '1px solid #10B981', borderRadius: 6, padding: '6px 10px', fontSize: 12, marginBottom: 8 }}>
          {pushResult}
        </div>
      )}
      {project.scenario && (
        <div style={{ fontSize: 12, color: '#B45309', marginBottom: 8 }}>
          Scenario job — punch list push is locked until the project is Won/Active.
        </div>
      )}

      <LaborSchedule project={project} />

      <div style={{ fontWeight: 600, fontSize: 13, margin: '8px 0 4px' }}>Equipment</div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Description</th>
            <th style={styles.th}>CAT-CLASS</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Qty</th>
            <th style={styles.th}>Start</th>
            <th style={styles.th}>End</th>
            <th style={styles.th}>Source</th>
            <th style={styles.th}></th>
          </tr>
        </thead>
        <tbody>
          {project.equipment_lines.map(l => (
            <tr key={`${l.source}-${l.id}`}>
              <td style={tdSm}>{l.description}</td>
              <td style={tdSm}>{l.cat_class || <span style={{ color: '#B45309' }}>—</span>}</td>
              <td style={{ ...tdSm, textAlign: 'right' }}>{l.qty}</td>
              <td style={tdSm}>{fmtDate(l.start_date) || 'job start'}</td>
              <td style={tdSm}>{fmtDate(l.end_date) || (l.num_days ? `${l.num_days} days` : 'job end')}</td>
              <td style={tdSm}>{l.source === 'estimate' ? 'Estimate' : 'Ops manual'}</td>
              <td style={tdSm}>
                {l.source === 'manual' && (
                  <button style={{ ...styles.btnSecondary, fontSize: 11, color: '#B91C1C' }} disabled={saving}
                    onClick={() => run(() => api.ganttDeleteEquipLine(l.id))}>✕</button>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td style={tdSm}>
              <input style={inSm} placeholder="Search catalog or type…" value={newEquip.description}
                onChange={e => {
                  setNewEquip({ ...newEquip, description: e.target.value, catalog_id: '' });
                  setCatalogQ(e.target.value);
                }} />
            </td>
            <td style={tdSm}><input style={{ ...inSm, width: 80 }} placeholder="350-14" value={newEquip.cat_class} onChange={e => setNewEquip({ ...newEquip, cat_class: e.target.value })} /></td>
            <td style={tdSm}><input style={{ ...inSm, width: 56, textAlign: 'right' }} placeholder="0" value={newEquip.qty} onChange={e => setNewEquip({ ...newEquip, qty: e.target.value })} /></td>
            <td style={tdSm}><input type="date" style={{ ...inSm, width: 130 }} value={newEquip.start_date} onChange={e => setNewEquip({ ...newEquip, start_date: e.target.value })} /></td>
            <td style={tdSm}><input type="date" style={{ ...inSm, width: 130 }} value={newEquip.end_date} onChange={e => setNewEquip({ ...newEquip, end_date: e.target.value })} /></td>
            <td style={tdSm} colSpan={2}>
              <button style={{ ...styles.btnSecondary, fontSize: 12 }} disabled={saving || !newEquip.description.trim()} onClick={addEquip}>➕ Add</button>
            </td>
          </tr>
        </tbody>
      </table>
      {catalogQ.trim() !== '' && catalog.length > 0 && (
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
          {catalog.slice(0, 30).map(item => (
            <div key={item.id}
              onClick={() => pickCatalogItem(item)}
              style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = '#FFF7ED'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span>
                <strong>{item.description}</strong> <span style={{ color: '#9CA3AF' }}>{item.category}</span>
                {item.cat_class && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: '#374151', background: '#E5E7EB', padding: '1px 5px', borderRadius: 6 }}>{item.cat_class}</span>}
              </span>
              {item.unit_count > 1 && <span style={{ color: '#6B7280', fontSize: 11, flexShrink: 0 }}>{item.unit_count} units</span>}
            </div>
          ))}
        </div>
      )}

      <div style={{ fontWeight: 600, fontSize: 13, margin: '12px 0 4px' }}>Labor</div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Craft</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Day HC</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Night HC</th>
            <th style={styles.th}>Start</th>
            <th style={styles.th}>End</th>
            <th style={styles.th}>Source</th>
            <th style={styles.th}></th>
          </tr>
        </thead>
        <tbody>
          {project.labor_lines.map((l, i) => (
            <tr key={l.source === 'manual' ? `m-${l.id}` : `e-${i}`}>
              <td style={tdSm}>{l.craft}</td>
              <td style={{ ...tdSm, textAlign: 'right' }}>{l.day_hc || 0}</td>
              <td style={{ ...tdSm, textAlign: 'right' }}>{l.night_hc || 0}</td>
              <td style={tdSm}>{fmtDate(l.start_date) || 'job start'}</td>
              <td style={tdSm}>{fmtDate(l.end_date) || 'job end'}</td>
              <td style={tdSm}>{l.source === 'estimate' ? 'Estimate' : 'Ops manual'}</td>
              <td style={tdSm}>
                {l.source === 'manual' && (
                  <button style={{ ...styles.btnSecondary, fontSize: 11, color: '#B91C1C' }} disabled={saving}
                    onClick={() => run(() => api.ganttDeleteLaborLine(l.id))}>✕</button>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td style={tdSm}><input style={inSm} placeholder="Add craft…" value={newLabor.craft} onChange={e => setNewLabor({ ...newLabor, craft: e.target.value })} /></td>
            <td style={tdSm}><input style={{ ...inSm, width: 56, textAlign: 'right' }} placeholder="0" value={newLabor.day_hc} onChange={e => setNewLabor({ ...newLabor, day_hc: e.target.value })} /></td>
            <td style={tdSm}><input style={{ ...inSm, width: 56, textAlign: 'right' }} placeholder="0" value={newLabor.night_hc} onChange={e => setNewLabor({ ...newLabor, night_hc: e.target.value })} /></td>
            <td style={tdSm}><input type="date" style={{ ...inSm, width: 130 }} value={newLabor.start_date} onChange={e => setNewLabor({ ...newLabor, start_date: e.target.value })} /></td>
            <td style={tdSm}><input type="date" style={{ ...inSm, width: 130 }} value={newLabor.end_date} onChange={e => setNewLabor({ ...newLabor, end_date: e.target.value })} /></td>
            <td style={tdSm} colSpan={2}>
              <button style={{ ...styles.btnSecondary, fontSize: 12 }} disabled={saving || !newLabor.craft.trim()} onClick={addLabor}>➕ Add</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
